import {
  infiniteQueryOptions,
  queryOptions,
  useIsMutating,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
  type Mutation,
  type QueryClient,
  type QueryKey,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useEffect } from 'react';

import { ensureFreshPlatformJwt } from '@/lib/sso.js';
import { filenameFromContentDisposition } from './insights.js';

/** edit = post replies automatically; ask = each reply waits for Approve/Reject. */
export type DeveloperMode = 'edit' | 'ask';

/** Claude SDK reasoning depth applied to every phase of one developer's executions. */
export type ReasoningEffort = 'low' | 'medium' | 'high' | 'xhigh' | 'max';

export const REASONING_EFFORTS: ReadonlyArray<{
  value: ReasoningEffort;
  label: string;
}> = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'xhigh', label: 'Extra high' },
  { value: 'max', label: 'Maximum' },
];

/**
 * The reasoning level each flow runs at when it has one of its own. A flow left out runs at the
 * developer's main `reasoningEffort`.
 */
export type FlowReasoningEfforts = Partial<
  Record<RequestType, ReasoningEffort>
>;

/**
 * The GitHub Actions workflow a Node.js developer watches after it merges its pull requests: an
 * `owner/name` repository and the workflow file name in it.
 */
export interface DeployPipeline {
  repository: string;
  workflow: string;
}

/**
 * Which credential the developer's runs are billed to. Both run the same Claude Agent SDK with the
 * same tools and prompts; only the credential differs. The `cursor` backend that used to sit beside
 * them was removed in September 2026.
 */
export type AgentProvider = 'claude' | 'claude-subscription';

/**
 * Which credential ran a run, **including backends that no longer exist**.
 *
 * A developer's `provider` is what it may be configured with today; a finished run records what
 * actually ran, and those are different questions. The service's Cursor migration backfills the
 * developers table and deliberately leaves `developer_runs.provider` alone, so `/runs` really can
 * answer `cursor` — and a type that denied it would let a reader switch exhaustively over two cases
 * and meet a third. It mirrors `RunProvider` in `cti-developer`; keep the two in step.
 */
export type RunProvider = AgentProvider | 'cursor';

/** The selectable agent providers. */
export const AGENT_PROVIDERS: ReadonlyArray<{
  value: AgentProvider;
  label: string;
}> = [
  { value: 'claude', label: 'Claude (Bedrock)' },
  { value: 'claude-subscription', label: 'Claude (subscription)' },
];

/**
 * What kind of engineer a developer is — it decides the flows it runs, the prompts it gets and the
 * tools it may use. `salesforce` is the default and what every developer written before this field
 * existed is; a `nodejs` developer builds with npm and has no Salesforce orgs at all; a `universal`
 * one works like `nodejs` in a repository of any language.
 */
export type DeveloperType = 'salesforce' | 'nodejs' | 'universal';

/** The selectable developer types. */
export const DEVELOPER_TYPES: ReadonlyArray<{
  value: DeveloperType;
  label: string;
}> = [
  { value: 'salesforce', label: 'Salesforce' },
  { value: 'nodejs', label: 'Node.js' },
  { value: 'universal', label: 'Universal' },
];

/**
 * Whether a developer of this type holds a terminal: it then has no Salesforce orgs and may watch a
 * deploy pipeline. Never a Salesforce developer.
 */
export function hasShell(type: DeveloperType): boolean {
  return type === 'nodejs' || type === 'universal';
}

/**
 * The role a developer plays in a Jira space — it gates the develop flow: `analyst` only asks
 * questions and posts a technical-solution plan; `developer` codes (asking only when a request is
 * unclear); `developer_analyst` does the full plan → approval → code cycle.
 */
export type SpaceRole = 'analyst' | 'developer' | 'developer_analyst';

/**
 * A status-transition trigger for one Jira issue type: the developer reacts when an issue of
 * `issueType` moves into any of the listed `statuses`. A trigger with no statuses does nothing and
 * is dropped before sending.
 */
export interface StatusTrigger {
  issueType: string;
  statuses: string[];
  /** When set, move the issue to this status once a run this trigger opened lands a PR. */
  targetStatus?: string;
}

/** One Jira space the developer watches, with the role it plays there. */
export interface JiraSpace {
  project: string;
  role: SpaceRole;
  statusTriggers?: StatusTrigger[];
  /** Free-text notes about this space, injected into the agent prompt. */
  description?: string;
  /** Move the issue to this status when the developer starts work on it. Ask mode skips the move. */
  startStatus?: string;
  /**
   * Move the issue to this status when the work finishes well: a reply is posted or a PR is opened.
   * A status trigger's own target status still wins for the PR that trigger opened.
   */
  finishStatus?: string;
}

/**
 * Which incoming events wake the developer, plus the switch that picks test or real requests.
 * `DEFAULT_TRIGGERED_ACTIONS` is the single source for the new-developer defaults.
 */
export interface TriggeredActions {
  jiraMention: boolean;
  jiraAssigned: boolean;
  githubMention: boolean;
  githubComment: boolean;
  /**
   * On: the developer answers only requests whose text contains `CTI_DEVELOPER_TEST_REQUEST`. Off:
   * it ignores those requests. This lets testers run e2e tests on shared Jira projects and repos
   * while the production developers there ignore them.
   */
  testRequestsOnly: boolean;
  /**
   * A new comment in one of the developer's Jira spaces names its alias. Absent on a developer saved
   * before the alias existed, which reads as off.
   */
  jiraAlias?: boolean;
  /**
   * A new comment on a pull or merge request in the developer's repositories names its alias. One
   * switch for GitHub and GitLab. Absent on a developer saved before it existed, which reads as off.
   */
  githubAlias?: boolean;
}

/**
 * New-developer defaults: Jira @-mention off (it's noisy), both alias triggers off (a new developer
 * has no alias) and test requests only off; the Jira assignment and both PR/MR triggers on.
 */
export const DEFAULT_TRIGGERED_ACTIONS: TriggeredActions = {
  jiraMention: false,
  jiraAssigned: true,
  githubMention: true,
  githubComment: true,
  testRequestsOnly: false,
  jiraAlias: false,
  githubAlias: false,
};

/**
 * A stored triggered-actions object with the defaults filled in key by key.
 *
 * A developer saved by an older service carries only the four event keys, and a response is cast,
 * not validated. A fallback for the whole object only covers a missing object, so it would leave the
 * new checkbox `checked={undefined}`. Merging key by key fills in only what is missing and keeps
 * every stored value.
 */
export function withTriggerDefaults(
  stored: Partial<TriggeredActions> | null | undefined,
): TriggeredActions {
  return { ...DEFAULT_TRIGGERED_ACTIONS, ...stored };
}

/** A Jira project the developer can watch, as returned by the Jira metadata endpoint. */
export interface JiraProject {
  key: string;
  name: string;
}

/** A Confluence space the developer can reach, as returned by the space metadata endpoint. */
export interface ConfluenceSpace {
  key: string;
  name?: string;
}

/** One Jira status: its display `name` and, when known, the workflow `category` (e.g. `In Progress`). */
export interface JiraStatus {
  name: string;
  category?: string;
}

/** The statuses available for one issue type in a Jira project. */
export interface JiraIssueTypeStatuses {
  issueType: string;
  statuses: JiraStatus[];
}

/** The kind of Salesforce org a developer connects to. */
export type SalesforceOrgType = 'developer' | 'full_sandbox' | 'production';

/** Whether the developer may write to a Salesforce org or only read from it. */
export type SalesforceOrgAccess = 'read' | 'read_write';

/**
 * A Salesforce org as returned on a developer view: config plus `hasSfdxUrl` (whether a secret is
 * stored). The decrypted `sfdxUrl` is never on a view — only on the export and in create/update input.
 */
export interface SalesforceOrg {
  id: string;
  name: string;
  /** What the org is for and what is allowed in it; injected into the agent's prompt. */
  description?: string;
  type: SalesforceOrgType;
  access: SalesforceOrgAccess;
  /** The repo whose changes deploy here (a `Repo.id`); absent means "any repo". */
  repoId?: string;
  /** The base branch that pairs with `repoId`; blank means "any branch of that repo". */
  branch: string;
  hasSfdxUrl: boolean;
}

/**
 * A Salesforce org in create/update input: `id` is sent on update so the backend keeps an unchanged
 * org's stored secret; `sfdxUrl` is sent only when (re)entered, so a blank keeps the stored secret.
 */
export interface SalesforceOrgInput {
  id?: string;
  name: string;
  description?: string;
  type: SalesforceOrgType;
  access: SalesforceOrgAccess;
  repoId?: string;
  branch?: string;
  sfdxUrl?: string;
  /**
   * The org's auth URL as an export carries it — sealed, so the file holds no usable credential. Sent
   * instead of `sfdxUrl` when an imported developer is saved without anybody retyping it; the service
   * opens it. A typed `sfdxUrl` wins, so the two are never sent together.
   */
  sfdxUrlSealed?: string;
}

/** A Salesforce org as carried in an export — its auth URL sealed, so the file holds no plaintext. */
export interface SalesforceOrgExport {
  id: string;
  name: string;
  description?: string;
  type: SalesforceOrgType;
  access: SalesforceOrgAccess;
  repoId?: string;
  branch: string;
  sfdxUrlSealed?: string;
}

/** The selectable Salesforce org types. */
export const SALESFORCE_ORG_TYPES: ReadonlyArray<{
  value: SalesforceOrgType;
  label: string;
}> = [
  { value: 'developer', label: 'Developer' },
  { value: 'full_sandbox', label: 'Full Sandbox' },
  { value: 'production', label: 'Production' },
];

/** The selectable Salesforce org access levels. */
export const SALESFORCE_ORG_ACCESS: ReadonlyArray<{
  value: SalesforceOrgAccess;
  label: string;
}> = [
  { value: 'read', label: 'Read only' },
  { value: 'read_write', label: 'Read / write' },
];

/** The version control host a repo lives on. */
export type RepoType = 'github' | 'gitlab';

/** Whether the developer may write to a repo or only read from it. */
export type RepoAccess = 'read' | 'read_write';

/**
 * One repository the developer works across. `id` is absent on new rows (the server mints it) and
 * present on a view. `url` is a slug or clone URL; `description` is free text injected into the agent
 * prompt.
 *
 * `access` is what decides the repo's role: **`read_write` makes it a working repo** — cloned as its own
 * checkout, and a run opens a PR in it if it changed anything there — and there can be many. `read`
 * repos are cloned read-only for cross-repo context.
 */
export interface Repo {
  id?: string;
  name: string;
  type: RepoType;
  url: string;
  description?: string;
  access: RepoAccess;
  defaultBranch: string;
  askBranch?: boolean;
  /** The GitHub login the developer answers as in this repo (GitHub repos only). */
  login?: string;
  /** A freshly entered PAT, sent on save only; never returned by the API. */
  pat?: string;
  /**
   * This repo's token as an export carries it — sealed. Sent instead of `pat` when an imported
   * developer is saved without anybody retyping it; a typed `pat` wins, so never both.
   */
  patSealed?: string;
  /** Whether a PAT is stored for this repo (view only — the token itself is never returned). */
  hasPat?: boolean;
  /**
   * The global secret this repo's token comes from. On a view, null when the repo holds its own
   * token; an older backend omits it. Sent on save in place of `pat` to link or keep a link.
   */
  credentialId?: string | null;
}

/** The selectable repo hosts. */
export const REPO_TYPES: ReadonlyArray<{ value: RepoType; label: string }> = [
  { value: 'github', label: 'GitHub' },
  { value: 'gitlab', label: 'GitLab' },
];

/** The selectable repo access levels. */
export const REPO_ACCESS: ReadonlyArray<{ value: RepoAccess; label: string }> =
  [
    { value: 'read', label: 'Read only (context)' },
    { value: 'read_write', label: 'Read / write (work here)' },
  ];

/** Live, derived state of a developer from its runs — drives the status icon in the UI. */
export type DeveloperStatus = 'idle' | 'running' | 'waiting' | 'error';

/** One developer's live stats row — spend, run counts, and time worked — for the global stats table. */
export interface DeveloperStat {
  id: string;
  name: string;
  status: DeveloperStatus;
  totalCostUsd: number;
  todayCostUsd: number;
  successCount: number;
  failedCount: number;
  /** Runs started today. Optional so an older backend that doesn't send it renders 0, not NaN. */
  todayRunCount?: number;
  timeWorkedTodayMs: number;
  /** What the developer is doing right now, as `<kind of work>: <step>`, when it's active. */
  currentActivity?: string;
}

/** The selectable roles for a Jira space, most-capable first. */
export const SPACE_ROLES: ReadonlyArray<{ value: SpaceRole; label: string }> = [
  { value: 'developer_analyst', label: 'Developer + Analyst' },
  { value: 'developer', label: 'Developer' },
  { value: 'analyst', label: 'Analyst' },
];

/**
 * The Bedrock Claude models a developer can run as. An empty id means "use the platform default"
 * (`DEV_AGENT_MODEL`). This is the single place to adjust the offered ids.
 *
 * **Every id here is a regional inference profile, checked against the verified catalog** in
 * `cti-admin-service/src/costs/model-catalog.ts`, which is the list confirmed against
 * `list-inference-profiles` in us-west-2. Nothing validates these on the way to Bedrock: an id that
 * does not resolve fails the run at call time, every time, for the developer that picked it.
 *
 * Two ids here were exactly that, and the shape of each mistake is worth remembering. Opus 4.5 was
 * not a profile at all. Haiku 4.5 dropped the dated suffix its real profile carries, the same slip
 * that made Bedrock answer `ValidationException: The provided model identifier is invalid` for Opus
 * 4.6 — so a suffix that looks like noise is load-bearing. Copy an id, never abbreviate one.
 */
export const BEDROCK_MODELS: ReadonlyArray<{ id: string; label: string }> = [
  { id: '', label: 'Default (platform)' },
  { id: 'us.anthropic.claude-opus-5-5', label: 'Claude Opus 5.5' },
  { id: 'us.anthropic.claude-opus-5', label: 'Claude Opus 5' },
  { id: 'us.anthropic.claude-opus-4-8', label: 'Claude Opus 4.8' },
  { id: 'us.anthropic.claude-opus-4-7', label: 'Claude Opus 4.7' },
  { id: 'us.anthropic.claude-opus-4-6-v1', label: 'Claude Opus 4.6' },
  { id: 'us.anthropic.claude-sonnet-5-5', label: 'Claude Sonnet 5.5' },
  { id: 'us.anthropic.claude-sonnet-5', label: 'Claude Sonnet 5' },
  { id: 'us.anthropic.claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
  { id: 'us.anthropic.claude-haiku-5-5', label: 'Claude Haiku 5.5' },
  {
    id: 'us.anthropic.claude-haiku-4-5-20251001-v1:0',
    label: 'Claude Haiku 4.5',
  },
];

/**
 * Model ids withdrawn from the picker, and what to do with saved selections.
 *
 * Correcting the list above only stops **new** selections. A developer saved before the correction
 * still loads its old value, the select keeps a value it does not recognise, and saving the form
 * sends it back unchanged — so that developer stays unable to run and the form quietly preserves the
 * thing breaking it. `normalizeBedrockModelId` is what closes that, applied both when a developer is
 * loaded into the form and when a payload is built from it.
 *
 * Haiku only lost the dated suffix its real profile carries, so it moves to that profile: the same
 * model, spelled correctly. Opus 4.5 has no profile at all behind it, so the platform default is the
 * only honest destination — and strictly better than what that developer has now, which cannot run.
 * The Global Opus 5.5 profile cannot be invoked with the platform's Bedrock IAM, which grants
 * `inference-profile/us.*` only, so it moves to the US profile: the same model, under the profile this
 * account can invoke, like the Haiku correction.
 */
const RETIRED_MODELS = new Map<string, string>([
  [
    'us.anthropic.claude-haiku-4-5',
    'us.anthropic.claude-haiku-4-5-20251001-v1:0',
  ],
  ['us.anthropic.claude-opus-4-5', ''],
  ['global.anthropic.claude-opus-5-5', 'us.anthropic.claude-opus-5-5'],
]);

/**
 * A stored model id, corrected when it is retired and returned untouched otherwise.
 *
 * Deliberately a **named list, not a membership test against `BEDROCK_MODELS`**. This picker offers a
 * subset of the profiles Bedrock serves, so an id missing from it is far more likely to be a valid
 * model somebody chose deliberately than a broken one. Rewriting everything unfamiliar would break
 * working configurations in order to fix the named retired choices.
 *
 * A `Map`, not an object literal, because an object lookup answers for every key `Object.prototype`
 * carries: an id of `toString` returned a **function** rather than the string that went in, and the
 * next thing to touch it calls `.trim()`. Settings are imported from a file a person can edit, so
 * that is reachable input rather than a hypothetical.
 *
 * It **trims before matching**, for the same reason. `optional` trims after this runs, so a padded
 * `" us.anthropic.claude-haiku-4-5 "` missed the exact-match map and was then trimmed straight back
 * into the retired id inside the create payload — the normalization defeated by the whitespace an
 * edited export file is exactly where you would find.
 */
export function normalizeBedrockModelId(id: string): string {
  const trimmed = id.trim();
  return RETIRED_MODELS.get(trimmed) ?? trimmed;
}

/**
 * The models a developer on a Claude Code seat can run as (provider=claude-subscription). Plain
 * Anthropic slugs, not Bedrock inference-profile ids: a seat authenticates against Anthropic's API,
 * where a `us.anthropic.…` id means nothing. An empty id means the platform default.
 */
export const ANTHROPIC_MODELS: ReadonlyArray<{ id: string; label: string }> = [
  { id: '', label: 'Default (platform)' },
  { id: 'claude-opus-5-5', label: 'Claude Opus 5.5' },
  { id: 'claude-opus-5', label: 'Claude Opus 5' },
  { id: 'claude-opus-4-8', label: 'Claude Opus 4.8' },
  { id: 'claude-sonnet-5-5', label: 'Claude Sonnet 5.5' },
  { id: 'claude-sonnet-5', label: 'Claude Sonnet 5' },
  { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' },
  { id: 'claude-haiku-5-5', label: 'Claude Haiku 5.5' },
  { id: 'claude-haiku-4-5', label: 'Claude Haiku 4.5' },
];

/**
 * What is known about a developer's stored Claude Code seat token. Mirrors `SeatTokenStatus` in
 * cti-developer, which is where the verdict is decided — this SPA renders it and never re-derives it,
 * so the two surfaces that show it cannot disagree about what "expired" means.
 */
export type SeatTokenStatus = 'none' | 'valid' | 'rejected' | 'unknown';

export interface Developer {
  id: string;
  name: string;
  jiraBaseUrl?: string;
  jiraAccountId?: string;
  /** A one-word name people use for the developer in a Jira comment. Absent, null or '' means none. */
  alias?: string | null;
  /**
   * The developer's own email domain for Jira mentions: only people with an email there are
   * mentioned. It wins over `jiraLinkedInnerDomain`. Null when unset, absent on an older API.
   */
  jiraInnerDomain?: string | null;
  /**
   * A read-only copy of the linked Jira global secret's domain, used when `jiraInnerDomain` is empty.
   * Never sent back. Null when there is none, absent on an older API.
   */
  jiraLinkedInnerDomain?: string | null;
  jiraSpaces?: JiraSpace[];
  /** The Confluence site the developer's wiki tools reach. Absent → it has no Confluence. */
  confluenceBaseUrl?: string;
  /** The account the Confluence token belongs to — the email, on an Atlassian Cloud site. */
  confluenceUsername?: string;
  /** The spaces the wiki tools may reach. Empty or absent means every space the token can see. */
  confluenceSpaces?: string[];
  triggeredActions?: TriggeredActions;
  /** The repositories this developer works across; each `read_write` one is a working repo. */
  repos?: Repo[];
  /** The Salesforce orgs this developer connects to (each with `hasSfdxUrl`; the url itself is never on a view). */
  salesforceOrgs?: SalesforceOrg[];
  /** What kind of engineer this is (absent on responses from a backend that predates it → salesforce). */
  type?: DeveloperType;
  /** Agent backend (absent on responses from a backend that predates the provider field → treat as claude). */
  provider?: AgentProvider;
  /** Model for the planning phase (empty → the platform default). */
  planningModel?: string;
  /** Model for implementation & PR building (empty → the platform default). */
  implementationModel?: string;
  /** Model for Jira/GitHub replies + Q&A (empty → the platform default). */
  replyModel?: string;
  /** Model for the task router / intent classifier (empty → the platform default). */
  routerModel?: string;
  /** Model a `#fast` run works on (empty → Claude Haiku 5.5). Absent on an older API. */
  fastModel?: string;
  /** Absent on an older API response; settings forms treat that as `medium`. */
  reasoningEffort?: ReasoningEffort;
  /** The flows with a reasoning level of their own; `{}` when none has one, absent on an older API. */
  flowReasoningEfforts?: FlowReasoningEfforts;
  /** Where a Node.js developer's merged pull requests deploy; null when unset, absent on an older API. */
  deployPipeline?: DeployPipeline | null;
  /** Free-text project-specific prompt, appended to every phase's system prompt. */
  projectPrompt?: string;
  /**
   * The workspace members who get a Teams message when a daily or after-save connection check
   * fails, as user ids. Absent on a backend that predates it, and the settings form then offers no
   * Contact persons field.
   */
  contactUserIds?: string[];
  hasJiraToken: boolean;
  /**
   * The global secret the Jira token comes from; null when the developer holds its own, absent on a
   * backend that predates global secrets. `hasJiraToken` stays true for a linked group.
   */
  jiraCredentialId?: string | null;
  /** Whether a Confluence token is stored (absent on a backend that predates it). */
  hasConfluenceToken?: boolean;
  /** The global secret the Confluence token comes from; null when it is the developer's own. */
  confluenceCredentialId?: string | null;
  hasSalesforce: boolean;
  /** Whether a Claude Code seat token is stored (absent on a backend that predates it). */
  hasClaudeToken?: boolean;
  /**
   * Whether the stored seat token still works. Derived by the service, never here: a seat token
   * expires and nothing refreshes it, so this shows the verdict a check reached rather than guessing
   * from a timestamp. `unknown` is a real answer — it means nothing has established anything, not
   * that the token is fine. Absent on a backend that predates it.
   */
  claudeTokenStatus?: SeatTokenStatus;
  /** When a check last confirmed the seat token, ISO — absent if none ever has. */
  claudeTokenValidAt?: string;
  /** When Anthropic last explicitly refused the seat token, ISO — absent if it never has. */
  claudeTokenRejectedAt?: string;
  /**
   * Whether a Firebase Crashlytics service-account key is stored. Absent on a backend that predates
   * Crashlytics, and the settings form reads that absence as "this backend cannot connect it".
   */
  hasCrashlyticsKey?: boolean;
  /** The Firebase project the stored Crashlytics key belongs to; null when none is stored. */
  crashlyticsProjectId?: string | null;
  /** The service account the stored Crashlytics key belongs to; null when none is stored. */
  crashlyticsClientEmail?: string | null;
  /** The global secret the Crashlytics key comes from; null when it is the developer's own. */
  crashlyticsCredentialId?: string | null;
  mode: DeveloperMode;
  enabled: boolean;
  status: DeveloperStatus;
  /** Total recorded model spend (USD) across this developer's runs — the running total (absent on
   * responses from a backend that predates cost tracking). */
  totalCostUsd?: number;
  pollIntervalSeconds?: number;
  /**
   * How many changes the developer develops at once, 1 to 10. Another approved change waits for a
   * free slot. Absent on a backend that predates slots, which the settings form reads as 2.
   */
  developmentSlots?: number;
  /**
   * On: after the plan is approved, the developer works until every pull request merges. Off: it
   * stops when the checks pass and hands the pull requests over. Absent on an older API, read as off.
   */
  mergePullRequests?: boolean;
  /**
   * How the developer names the branch it publishes, in the operator's words. A name the requester asks
   * for wins; this wins over the repository's own conventions. Null when unset, absent on an older API.
   */
  branchNameInstructions?: string | null;
  /** How the developer titles its pull requests, ranked like `branchNameInstructions`. */
  prTitleInstructions?: string | null;
  /** How the developer writes its pull request descriptions, ranked like `branchNameInstructions`. */
  prDescriptionInstructions?: string | null;
  jiraPolledAt?: string;
  githubPolledAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface DeveloperCreateInput {
  name: string;
  jiraBaseUrl?: string;
  jiraAccountId?: string;
  /** Trimmed. Left out of a create when blank; `''` on an update clears it. */
  alias?: string;
  /** The email domain for Jira mentions, trimmed. Left out of a create when blank; `''` on an update clears it. */
  jiraInnerDomain?: string;
  jiraSpaces?: JiraSpace[];
  /** The Confluence site the developer's wiki tools reach. Absent → it has no Confluence. */
  confluenceBaseUrl?: string;
  /** The account the Confluence token belongs to — the email, on an Atlassian Cloud site. */
  confluenceUsername?: string;
  /** The spaces the wiki tools may reach. Empty or absent means every space the token can see. */
  confluenceSpaces?: string[];
  triggeredActions?: TriggeredActions;
  repos?: Repo[];
  type?: DeveloperType;
  provider?: AgentProvider;
  planningModel?: string;
  implementationModel?: string;
  replyModel?: string;
  routerModel?: string;
  fastModel?: string;
  reasoningEffort: ReasoningEffort;
  /**
   * The whole per-flow map, which the service stores in place of the old one. `null` or `{}` clears
   * it; an absent field keeps what is stored on an update.
   */
  flowReasoningEfforts?: FlowReasoningEfforts | null;
  /** A Node.js developer's deploy pipeline; `null` clears it. The service drops it for Salesforce. */
  deployPipeline?: DeployPipeline | null;
  projectPrompt?: string;
  salesforceOrgs?: SalesforceOrgInput[];
  /**
   * Optional **only** because an imported export sends `jiraTokenSealed` in its place — the service
   * refuses a payload carrying neither.
   */
  jiraToken?: string;
  /** The Jira token as an export carries it, sealed. A typed `jiraToken` wins, so never both. */
  jiraTokenSealed?: string;
  /**
   * The global secret to link Jira to, or the stored one again to keep the link. Sent in place of a
   * token, never beside one: the service refuses both together, and a new id needs `admin`.
   */
  jiraCredentialId?: string;
  /** The same for Confluence. */
  confluenceCredentialId?: string;
  /** The same for Crashlytics, in place of `crashlyticsServiceAccount`. */
  crashlyticsCredentialId?: string;
  /**
   * The Confluence API token or PAT. Optional in every case, unlike `jiraToken`: a developer without
   * one is an ordinary developer that simply has no Confluence tools.
   */
  confluenceToken?: string;
  /**
   * **Not a field of a create or an update.** An export carries a sealed Confluence token and the
   * service imports it on no path — the ciphertext is not bound to the site it was issued for, so an
   * import could pair one developer's credential with any host. Typed in instead; the export type
   * below still carries it, because the file records it.
   */
  claudeToken?: string;
  /**
   * The text of a Google service-account JSON key file, to connect or replace Crashlytics. `''`
   * disconnects it; an absent key keeps what is stored.
   */
  crashlyticsServiceAccount?: string;
  mode?: DeveloperMode;
  enabled?: boolean;
  pollIntervalSeconds?: number;
  /** An integer from 1 to 10. A backend that predates slots drops the key without a word. */
  developmentSlots?: number;
  /** Whether the developer merges its own pull requests. The service starts every developer off. */
  mergePullRequests: boolean;
  /**
   * How the developer names the branch it publishes: at most 4000 characters, trimmed. Left out of a
   * create when blank; `''` on an update clears it and an omitted one keeps what is stored.
   */
  branchNameInstructions?: string;
  /** How the developer titles its pull requests, sent like `branchNameInstructions`. */
  prTitleInstructions?: string;
  /** How the developer writes its pull request descriptions, sent like `branchNameInstructions`. */
  prDescriptionInstructions?: string;
}

/**
 * The full portable config for a developer as returned by `GET /:id/export`, with every credential
 * **sealed** rather than decrypted.
 *
 * It used to carry plaintext tokens, and the file is downloaded to somebody's disk — so what it holds
 * now is the ciphertext the server already stored, which the create form submits back on the `*Sealed`
 * keys and the service opens. Nothing in the file is usable to whoever opens it, and it only imports
 * on an instance that shares the same vault key.
 */
export interface DeveloperExport {
  name: string;
  jiraBaseUrl?: string;
  jiraAccountId?: string;
  /** Absent on a file taken before the alias existed. */
  alias?: string;
  /** Absent on a file taken before the inner domain existed. */
  jiraInnerDomain?: string;
  jiraSpaces?: JiraSpace[];
  triggeredActions?: TriggeredActions;
  repos?: Repo[];
  type?: DeveloperType;
  provider?: AgentProvider;
  planningModel?: string;
  implementationModel?: string;
  replyModel?: string;
  routerModel?: string;
  /** Absent on a file taken before the fast mode existed. */
  fastModel?: string;
  reasoningEffort?: ReasoningEffort;
  /** Present only when a flow has a level of its own, so an older file has none. */
  flowReasoningEfforts?: FlowReasoningEfforts;
  /** Present only when a Node.js developer has one, so an older file has none. */
  deployPipeline?: DeployPipeline;
  salesforceOrgs?: SalesforceOrgExport[];
  mode: DeveloperMode;
  pollIntervalSeconds?: number;
  /** Absent on a file whose service did not export it; an import then keeps the form default. */
  developmentSlots?: number;
  /** Absent on a file whose service did not export it; an import then keeps the setting off. */
  mergePullRequests?: boolean;
  /** Present only when it is set, so an older file has none. */
  branchNameInstructions?: string;
  /** Present only when it is set, so an older file has none. */
  prTitleInstructions?: string;
  /** Present only when it is set, so an older file has none. */
  prDescriptionInstructions?: string;
  /** Present only when one was picked; an import writes it after the create. */
  proseSkill?: string;
  /** Present only when a box is set; an import writes each after the create. */
  proseInstructions?: Partial<Record<ProseScope, string>>;
  jiraTokenSealed?: string;
  /** A Jira linked to a global secret carries the entry's id and no sealed token. */
  jiraCredentialId?: string | null;
  /** The same for Confluence. */
  confluenceCredentialId?: string | null;
  /** The same for Crashlytics. */
  crashlyticsCredentialId?: string | null;
  confluenceBaseUrl?: string;
  confluenceUsername?: string;
  confluenceSpaces?: string[];
  /** The Confluence token, sealed. Imported on the `confluenceTokenSealed` key of a create. */
  confluenceTokenSealed?: string;
  /**
   * Present only in a file taken **before** the export was sealed, which still has to import. The
   * service never emits it any more, and the importer reads both for exactly that reason.
   */
  confluenceToken?: string;
  /**
   * Present in the file and **not** carried into an import, which is deliberate rather than a gap
   * this type forgot.
   *
   * The seat credential is no longer a per-developer setting — it moved to the global provider pool,
   * and `CreateDeveloperDto` drops `claudeToken` and `provider` on purpose so no import can pin one
   * developer to a credential the fleet has moved off. So `fromSettingsJson` does not read this and
   * the form has no field for it; carrying it through would undo that decision. It is typed here so
   * a reader of the file knows what the file holds.
   */
  claudeTokenSealed?: string;
}

export type DeveloperUpdateInput = Partial<DeveloperCreateInput> & {
  /** The contact persons, as workspace user ids. Update only: a new developer has no workspace yet. */
  contactUserIds?: string[];
};

export type RunStatus =
  | 'queued'
  | 'running'
  | 'awaiting_approval'
  | 'replied'
  | 'rejected'
  | 'planning'
  | 'awaiting_info'
  | 'awaiting_plan_approval'
  | 'awaiting_credentials'
  | 'awaiting_checks'
  | 'awaiting_slot'
  | 'developing'
  | 'pr_opened'
  | 'stopped'
  | 'failed';

/**
 * A run that has ended — nothing more happens to it. Mirrors `TERMINAL_RUN_STATUSES` in cti-developer.
 *
 * Liveness checks are written against **this** list rather than the active one, so that a status this
 * build has never seen (responses are cast, not validated) reads as *still going*. The other way round,
 * a newer server's in-flight status would silently drop the poll and hide the Stop action.
 */
export const TERMINAL_RUN_STATUSES: readonly RunStatus[] = [
  'replied',
  'rejected',
  'pr_opened',
  'stopped',
  'failed',
];

/** What the work item a run was opened for is — the service classifies it from the Jira issue type. */
export type WorkItemType = 'defect' | 'change' | 'inquiry';

/** What kind of activity a run represents — drives the History/Activity entry icon + label. */
export type RunEntryType =
  | 'jira_reply'
  | 'github_reply'
  | 'developing'
  | 'pr_opened'
  | 'pr_updated'
  | 'deployment'
  | 'review'
  | 'other';

/** The kind of request a developer run works through — selects which flow (step list) it follows. */
export type RequestType =
  'question' | 'analyze_plan' | 'develop' | 'pr_review' | 'code_review';

/**
 * The request type a run can carry. A Code Quality evaluation runs as `code_quality`: it follows a
 * prose instruction only and has no steps, so it is not a flow and stays out of `REQUEST_TYPES`.
 */
export type RunRequestType = RequestType | 'code_quality';

/** The request types with their display labels, in flow order — feeds the read-only Steps sub-tabs. */
export const REQUEST_TYPES: ReadonlyArray<{
  value: RequestType;
  label: string;
}> = [
  { value: 'question', label: 'Question' },
  { value: 'analyze_plan', label: 'Analyze & Plan' },
  { value: 'develop', label: 'Develop' },
  { value: 'pr_review', label: 'PR Review' },
  { value: 'code_review', label: 'Code Review' },
];

/**
 * One ordered step in a flow: the step id, its title, and what the agent should do. The id is the join
 * key for a run's live per-step status, and the agent's `[[step:<id>:done]]` marker only recognises
 * `[a-z_]`, so it may hold nothing else. Every step is optional: the developer takes only the ones a
 * request needs.
 */
export interface FlowStep {
  id: string;
  title: string;
  instruction: string;
}

/** The flows keyed by request type — the ordered steps each request type runs through. */
export type DeveloperFlows = Record<RequestType, FlowStep[]>;

/**
 * One developer's resolved flows plus which request types it actually overrides. `customized` is what
 * distinguishes an override that happens to equal the platform default from no override at all — the
 * Customised badge and whether Reset would do anything both key on it.
 */
export interface DeveloperFlowsView {
  flows: DeveloperFlows;
  customized: RequestType[];
}

/**
 * The seven places a developer adds an instruction of its own: one per flow a run can follow,
 * `general`, which is added to every flow so an instruction that should hold everywhere is written
 * once, and `code_quality`, the instruction of a Code Quality evaluation, which gets no `general`.
 * The flows are the Steps tab's axis, so a developer shapes a flow's steps and its instruction in the
 * same terms.
 */
export type PromptScope = 'general' | RunRequestType;

/** The prompt scopes with their labels, in flow order: the Instructions sub-tabs, then `code_quality` for the Code Quality tab. */
export const PROMPT_SCOPES: ReadonlyArray<{
  value: PromptScope;
  label: string;
  /** One line under the sub-tab saying which work this instruction opens. */
  hint: string;
}> = [
  {
    value: 'general',
    label: 'General',
    hint: "Added to every flow, after the platform's own instruction.",
  },
  {
    value: 'question',
    label: 'Question',
    hint: "Investigating read-only to answer a question, without changing any code. Added after the platform's instruction for this flow.",
  },
  {
    value: 'analyze_plan',
    label: 'Analyze & Plan',
    hint: "Deciding the next step: ask, propose a plan, or start coding. Added after the platform's instruction for this flow.",
  },
  {
    value: 'develop',
    label: 'Develop',
    hint: "Writing the change. Added after the platform's instruction for this flow.",
  },
  {
    value: 'pr_review',
    label: 'PR Review',
    hint: "Addressing review comments on an open pull request until it comes back clean. Added after the platform's instruction for this flow.",
  },
  {
    value: 'code_review',
    label: 'Code Review',
    hint: "Reviewing a pull request, a merge request or a piece of code and posting the findings, without changing anything. Added after the platform's instruction for this flow.",
  },
  {
    value: 'code_quality',
    label: 'Code quality review',
    hint: "Scoring a repository for Code Quality without changing any code. Added after the platform's instruction for the evaluation, which brings its own checklist from the Code Quality service. General is not added to this instruction, so scores stay comparable between developers.",
  },
];

/** The most characters the service accepts in one instruction box. */
export const INSTRUCTION_MAX_LENGTH = 4000;

/** One developer's own instructions keyed by flow; `''` where it adds nothing. */
export type DeveloperInstructions = Record<PromptScope, string>;

/**
 * One developer's own instructions, each added after the platform's instruction for its flow, plus
 * `legacy`: the flows where an older full replacement of the platform instruction is still active.
 */
export interface DeveloperInstructionsView {
  instructions: DeveloperInstructions;
  legacy: PromptScope[];
}

/** The skill of a developer's first prose pass, which shapes how every text it writes reads. */
export type ProseSkill = 'humanizer' | 'i-have-adhd' | 'ai-output-humanizer';

/** The prose skills on offer, the default first, each with one line on what it does to the text. */
export const PROSE_SKILLS: ReadonlyArray<{
  value: ProseSkill;
  label: string;
  hint: string;
}> = [
  {
    value: 'humanizer',
    label: 'Humanizer (default)',
    hint: 'Removes AI writing patterns.',
  },
  {
    value: 'i-have-adhd',
    label: 'i-have-adhd',
    hint: 'Leads with the next action, numbered steps, short lists.',
  },
  {
    value: 'ai-output-humanizer',
    label: 'AI Output Humanizer',
    hint: 'A stricter audit of AI writing patterns.',
  },
];

/** The skill of a developer that never picked one. */
const DEFAULT_PROSE_SKILL: ProseSkill = 'humanizer';

/** The places a developer adds a prose instruction: General and one per flow, but not Code Quality. */
export type ProseScope = Exclude<PromptScope, 'code_quality'>;

/** The prose boxes in flow order, with the Instructions sub-tab labels and a line on what each shapes. */
export const PROSE_SCOPES: ReadonlyArray<{
  value: ProseScope;
  label: string;
  hint: string;
}> = PROMPT_SCOPES.flatMap((scope) =>
  scope.value === 'code_quality'
    ? []
    : [
        {
          value: scope.value,
          label: scope.label,
          hint:
            scope.value === 'general'
              ? 'How every text this developer writes should read, in every flow. For example: use bullet points, use tables, keep a conversational tone.'
              : 'How every text this developer writes in this flow should read, added to General. For example: use bullet points, use tables, keep a conversational tone.',
        },
      ],
);

/** One developer's prose instructions keyed by scope; `''` where it adds nothing. */
export type DeveloperProseInstructions = Record<ProseScope, string>;

/** A developer's prose settings: its skill, which can be one this build does not offer, and its boxes. */
export interface DeveloperProseView {
  skill: string;
  instructions: DeveloperProseInstructions;
}

/** The prose a settings file carries, which the create call does not take. */
export interface ProseImport {
  proseSkill?: ProseSkill;
  proseInstructions?: Partial<Record<ProseScope, string>>;
}

/** The live status of one step in a run's flow. */
export type RunStepStatus = 'queued' | 'in_progress' | 'done' | 'skipped';

/** One step of a run's flow with its live status — carried on a run and on `flow` activity events. */
export interface RunStep {
  id: string;
  title: string;
  status: RunStepStatus;
}

/**
 * Why an approved run waits to develop: every development slot is busy, or the disk has too little
 * free space. API-cast, so a newer server may send another value.
 */
export type SlotWaitReason = 'slot' | 'disk';

/** What an `awaiting_slot` run waits for, and since when. */
export interface SlotWait {
  reason: SlotWaitReason;
  /** When the run started waiting, ISO. */
  since: string;
  /** Free space on the workspace volume when last checked, on a disk wait. */
  freeBytes?: number;
  /** The free space development needs, on a disk wait. */
  neededBytes?: number;
}

/**
 * The reasoning mode a `#fast`, `#think` or `#ultra` tag set for a whole run. API-cast, so a newer
 * server may send another value.
 */
export type ReasoningMode = 'fast' | 'think' | 'ultra';

export interface DeveloperRun {
  id: string;
  developerId: string;
  /** The developer chat thread a `manual` run belongs to. Absent on the cross-developer feed. */
  conversationId?: string | null;
  source: 'jira' | 'github' | 'manual';
  triggerRef: string;
  question: string;
  /**
   * The Jira issue the run came from, built by the service from the developer's own base url. Absent
   * for a GitHub or chat run, and for a developer with no Jira base url configured.
   */
  ticketUrl?: string;
  kind: 'question' | 'develop';
  status: RunStatus;
  /** Present only on an `awaiting_slot` run; null or absent on every other one. */
  slotWait?: SlotWait | null;
  /**
   * Only the sessions list carries this: the status still says executing, but no process works the
   * run any more. Optional because an older service answers without it.
   */
  stale?: boolean;
  /** Which request type this run works through (absent on legacy runs). */
  requestType?: RunRequestType;
  /** The reasoning mode a tag set for the run; absent when none did. */
  reasoningMode?: ReasoningMode;
  /** What the work item is — defect, change or inquiry (absent on legacy runs and older backends). */
  workItemType?: WorkItemType;
  /** The run's flow steps with their live status (absent on legacy runs). */
  steps?: RunStep[];
  /** A one-line summary of the run's subject (e.g. the Jira issue summary), when the backend supplies it. */
  summary?: string;
  /** The pull-request title for a run that opened/updated a PR, when known. */
  prTitle?: string;
  /** What kind of activity this run is — drives the entry icon/label (absent on legacy runs). */
  entryType?: RunEntryType;
  /** Whether this run resulted in a deployment. */
  deployed: boolean;
  reply?: string;
  pendingAction?: 'reply' | 'questions' | 'plan' | 'publish';
  /** The agent's workflow-checklist report for this run, when it followed a workflow. */
  workflowReport?: string;
  error?: string;
  /** The first PR the run opened — kept for readers that show a single link. */
  prUrl?: string;
  /** Every PR the run opened, one per working repo it changed. */
  prUrls?: string[];
  posted: boolean;
  /** Which credential ran this run (absent on legacy runs) — wider than a developer's own setting. */
  provider?: RunProvider;
  /** Estimated model spend for the run (USD); absent on a legacy or otherwise unmeasured run. */
  costUsd?: number;
  inputTokens?: number;
  outputTokens?: number;
  /** Input tokens read from the prompt cache. Absent or null on a run recorded before it existed. */
  cacheReadTokens?: number | null;
  /** Input tokens written to the prompt cache. Absent or null on a run recorded before it existed. */
  cacheCreationTokens?: number | null;
  durationMs?: number;
  /**
   * The share of the developer's Claude Code seat five-hour allowance this run spent, in percentage
   * points. Present only on a `claude-subscription` run the service could measure — a seat is
   * already paid for, so its scarce resource is the allowance, not money, and this is what a seat run
   * shows where a metered run shows a price.
   *
   * Absent is not zero: a metered run and a legacy run both send nothing, and so does a seat run
   * neither Anthropic nor a configured allowance could account for.
   */
  claude5hourPercent?: number;
  /**
   * Until when the run's session archive can be downloaded, ISO. Null when there is none, it expired,
   * or this caller may not download it. Absent from an older service, which reads the same as null.
   */
  sessionArchiveExpiresAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * One run as the activity feed reads it — `DeveloperRun` without the prose fields the feed never
 * renders. Structural, not a separate contract: a reader that wants the reply or the flow steps opens
 * the run's log.
 */
export type ActivityRun = Omit<
  DeveloperRun,
  'reply' | 'steps' | 'workflowReport'
>;

/**
 * One window of cross-developer activity.
 *
 * Two flags carry the states an empty `runs` array cannot distinguish, and both must reach the user:
 * `truncated` means the window held more runs than the server would return; `unsupported` means the
 * backend predates the endpoint, which otherwise reads as a week in which nobody did anything.
 */
export interface ActivityWindow {
  from: string;
  to: string;
  truncated: boolean;
  runs: ActivityRun[];
  unsupported: boolean;
}

/**
 * A suggested history action: recent (≤3 days) work from *before* the developer was created — a comment,
 * @-mention, or status transition that never auto-ran but can be run on demand.
 */
export interface Suggestion {
  source: 'jira' | 'github';
  kind: 'status' | 'mention' | 'comment';
  triggerRef: string;
  ref: string;
  title: string;
  question: string;
  role?: SpaceRole;
  at?: string;
}

/** Per-service result of a connection check. */
export interface ConnectionStatus {
  configured: boolean;
  ok: boolean;
  detail?: string;
}

/** Per-Jira-space connection result — the project key plus the shared configured/ok/detail shape. */
export interface JiraSpaceConnectionStatus extends ConnectionStatus {
  project: string;
}

/** Per-repo connection result — the repo id + name plus the shared configured/ok/detail shape. */
export interface RepoConnectionStatus extends ConnectionStatus {
  id: string;
  name: string;
}

/** One Confluence space's reachability, carrying the key alongside its status. */
export interface ConfluenceSpaceConnectionStatus extends ConnectionStatus {
  space: string;
}

export interface ConnectionReport {
  jira: ConnectionStatus;
  /** Every configured repo of either provider. Absent on a backend that still sends `github`. */
  versionControl?: ConnectionStatus;
  /** The name `versionControl` used to have. Absent once the backend is renamed. */
  github?: ConnectionStatus;
  salesforce: ConnectionStatus;
  /**
   * Confluence reachability. Optional because this SPA deploys separately from `cti-developer`, so
   * for one release the field may not arrive at all — a reader must treat absent as "this backend
   * does not check Confluence", never as "Confluence is broken".
   */
  confluence?: ConnectionStatus;
  /** Crashlytics reachability. Absent on a backend that predates it, which means "not checked". */
  crashlytics?: ConnectionStatus;
  /** Per-space Jira reachability, rendered under the Jira row (absent on a backend that predates it). */
  jiraSpaces?: JiraSpaceConnectionStatus[];
  /**
   * Per-space Confluence reachability. The admin panel renders it under the Confluence row; this
   * SPA's Connection status popover shows the aggregate only, as it does for `jiraSpaces` and `repos`.
   */
  confluenceSpaces?: ConfluenceSpaceConnectionStatus[];
  /** Per-repo reachability, rendered under the Version Control row (absent on a backend that predates it). */
  repos?: RepoConnectionStatus[];
}

/**
 * The version-control aggregate under whichever name the backend sent it.
 *
 * The field is being renamed from `github` to `versionControl`, and this SPA deploys separately from
 * `cti-developer`, so for one release either name can arrive. Delete this helper and the `github`
 * field together once every environment runs the renamed service.
 */
export function versionControlStatus(
  report: ConnectionReport,
): ConnectionStatus {
  return (
    report.versionControl ?? report.github ?? { configured: false, ok: false }
  );
}

/** What started a connection check. API-cast: a newer server may send a value outside this list. */
export type ConnectionCheckTrigger = 'save' | 'manual' | 'daily';

/** One finished background connection check. */
export interface ConnectionCheckResult {
  outcome: 'ok' | 'failed';
  trigger: ConnectionCheckTrigger;
  startedAt: string;
  finishedAt: string;
  /** Null when the check itself could not run; `error` then says why. */
  report: ConnectionReport | null;
  error: string | null;
}

/** The overall look of a developer's connections. API-cast: a newer server may send another value. */
export type ConnectionCheckState = 'not_checked' | 'running' | 'ok' | 'failed';

/** The developer's connection check as the server keeps it: the one running now and the last result. */
export interface ConnectionCheckView {
  state: ConnectionCheckState;
  running: { trigger: ConnectionCheckTrigger; startedAt: string } | null;
  /** A save landed during the running check, so one more check follows it. */
  recheckQueued: boolean;
  /** Kept while a new check runs, so the previous answer can still be shown. */
  last: ConnectionCheckResult | null;
}

/** Result of saving a repo doc: the PR url and whether it auto-merged. */
export interface SaveRepoDocResult {
  prUrl: string;
  merged: boolean;
}

/** One line of the developer's streaming activity log. */
export interface ActivityEvent {
  /** A stored line's id. A line read back from a run's stored log has one; a streamed line has none. */
  id?: string;
  level: string;
  message: string;
  runId?: string;
  reply?: string;
  posted?: boolean;
  /** Server timestamp (ISO) of when the action happened; survives SSE replay, unlike arrival time. */
  at?: string;
  /** On a `flow` event: the request type the run started/resumed on. */
  requestType?: RunRequestType;
  /** On a `flow` event: the run's full step list with each step's current status. */
  steps?: RunStep[];
  /** On a `step` event: which step changed. */
  stepId?: string;
  /** On a `step` event: the step's new status (message carries the step title). */
  status?: RunStepStatus;
}

const ENDPOINT = '/v1/developers';

/**
 * Identity travels only as the platform JWT bearer — the gateway + service verify it against
 * identity's JWKS and strip any client-supplied identity headers. Reads the bearer from the admin
 * auth store, matching the SSE bearer pattern in admin-event-stream / quality-run-stream.
 */
async function authHeaders(): Promise<Record<string, string>> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const jwt = await ensureFreshPlatformJwt();
  if (jwt) h['Authorization'] = `Bearer ${jwt}`;
  return h;
}

async function errorMessage(res: Response, fallback: string): Promise<string> {
  const text = await res.text().catch(() => '');
  try {
    const body = JSON.parse(text) as { message?: string | string[] };
    /** NestJS ValidationPipe returns `message` as a string array — join it rather than render "[object]". */
    if (Array.isArray(body.message) && body.message.length) {
      return body.message.join('; ');
    }
    if (typeof body.message === 'string' && body.message) return body.message;
  } catch {
    if (text.trim()) return text.trim();
  }
  return `${fallback} (${res.status})`;
}

async function fetchDevelopers(): Promise<Developer[]> {
  const res = await fetch(ENDPOINT, { headers: await authHeaders() });
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to load developers'));
  return (await res.json()) as Developer[];
}

/** Empty flows object with every request type mapped to `[]`, for an older backend without `/flows`. */
function emptyDeveloperFlows(): DeveloperFlows {
  return {
    question: [],
    analyze_plan: [],
    develop: [],
    pr_review: [],
    code_review: [],
  };
}

/**
 * The read-only per-request-type flows (step lists) of one kind of developer — there is a catalog per
 * developer type, and this asks for one of them. An older backend without this endpoint answers 404;
 * treat that as "unsupported" and return an empty flows object so the Steps sub-tabs render empty
 * instead of erroring. Every other error still throws.
 */
async function fetchFlows(type: DeveloperType): Promise<DeveloperFlows> {
  const res = await fetch(`${ENDPOINT}/flows?type=${type}`, {
    headers: await authHeaders(),
  });
  if (res.status === 404) return emptyDeveloperFlows();
  if (!res.ok) throw new Error(await errorMessage(res, 'Failed to load flows'));
  return (await res.json()) as DeveloperFlows;
}

/**
 * One developer's resolved flows: its own overrides where set, the platform defaults elsewhere.
 *
 * A 404 degrades to empty flows for the same reason `fetchFlows` does — an older backend has no such
 * route. That tolerance cannot mask a missing developer here, because the detail page only renders once
 * its developer query has resolved. Missing request types are filled in so a key can never read back
 * `undefined` under `noUncheckedIndexedAccess`.
 */
async function fetchDeveloperFlows(id: string): Promise<DeveloperFlowsView> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}/flows`, {
    headers: await authHeaders(),
  });
  if (res.status === 404)
    return { flows: emptyDeveloperFlows(), customized: [] };
  if (!res.ok) throw new Error(await errorMessage(res, 'Failed to load flows'));
  return normalizeFlowsView(await res.json());
}

/**
 * Coerce a flows response into the shape the UI can rely on. Every one of these responses is type-*cast*
 * rather than validated, so both fields are untrusted: spreading a string would produce a character map
 * instead of flows, a partial map would briefly empty the other request types, and a non-array
 * `customized` would break `new Set(...)` on the next render. Applied to the write responses too — those
 * go straight into the query cache, so they are the *more* dangerous path, not the lesser one.
 */
function normalizeFlowsView(body: unknown): DeveloperFlowsView {
  const view = (body ?? {}) as Partial<DeveloperFlowsView>;
  const flows =
    view.flows && typeof view.flows === 'object' && !Array.isArray(view.flows)
      ? view.flows
      : {};
  return {
    flows: { ...emptyDeveloperFlows(), ...flows },
    customized: Array.isArray(view.customized) ? view.customized : [],
  };
}

/** The steps a client sends for one flow — a new step omits its id and the server names it. */
export interface FlowStepInput {
  id?: string;
  title: string;
  instruction: string;
}

/**
 * Replace one request type's flow for a developer. Scoped to a single request type, matching the
 * backend: a whole-map write would either force the client to echo the other three back — freezing
 * today's platform text in as a permanent override — or silently wipe them. Unlike the read, a failure
 * here throws: a save that silently did nothing is worse than an error the user can see.
 */
async function saveDeveloperFlow(args: {
  id: string;
  requestType: RequestType;
  steps: FlowStepInput[];
}): Promise<DeveloperFlowsView> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/flows/${args.requestType}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
      body: JSON.stringify({ steps: args.steps }),
    },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to save the flow'));
  return normalizeFlowsView(await res.json());
}

/** Drop one request type's override so it follows the platform default again — and keeps following it. */
async function resetDeveloperFlow(args: {
  id: string;
  requestType: RequestType;
}): Promise<DeveloperFlowsView> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/flows/${args.requestType}`,
    { method: 'DELETE', headers: await authHeaders() },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to reset the flow'));
  return normalizeFlowsView(await res.json());
}

/** Every flow mapped to `''`: no instruction added anywhere, as an older backend without the route reads. */
function emptyDeveloperInstructions(): DeveloperInstructions {
  return {
    general: '',
    question: '',
    analyze_plan: '',
    develop: '',
    pr_review: '',
    code_review: '',
    code_quality: '',
  };
}

/** Whether a value is a plain object, which a `{ ... }` field of a cast response has to be to read from. */
function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Coerce an instructions response into the shape the editor can rely on. Cast rather than validated,
 * so every field is untrusted: each value goes straight into a controlled `<Textarea>`, so a missing
 * flow or a non-string reads as `''`, and `legacy` keeps only the flows this build knows. Applied to
 * the write responses too, because those go straight into the query cache.
 */
function normalizeInstructionsView(body: unknown): DeveloperInstructionsView {
  const view = isPlainObject(body) ? body : {};
  const stored = isPlainObject(view.instructions) ? view.instructions : {};
  const instructions = emptyDeveloperInstructions();
  for (const scope of PROMPT_SCOPES) {
    const value = stored[scope.value];
    if (typeof value === 'string') instructions[scope.value] = value;
  }
  const known = new Set<unknown>(PROMPT_SCOPES.map((scope) => scope.value));
  const legacy = Array.isArray(view.legacy)
    ? (view.legacy.filter((scope) => known.has(scope)) as PromptScope[])
    : [];
  return { instructions, legacy };
}

/**
 * One developer's own instructions. A 404 degrades to an empty view for the same reason
 * `fetchDeveloperFlows` does: an older backend has no such route, and the detail page only renders
 * once its developer query has resolved, so this cannot hide a missing developer.
 */
async function fetchDeveloperInstructions(
  id: string,
): Promise<DeveloperInstructionsView> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(id)}/instructions`,
    { headers: await authHeaders() },
  );
  if (res.status === 404) return normalizeInstructionsView(undefined);
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to load instructions'));
  return normalizeInstructionsView(await res.json());
}

/**
 * Store one flow's instruction, which the service adds after the platform's own. Scoped to one flow,
 * like the service. A blank instruction clears it. A failure throws, because a save that silently did
 * nothing is worse than an error the user can see.
 */
async function saveDeveloperInstruction(args: {
  id: string;
  scope: PromptScope;
  instruction: string;
}): Promise<DeveloperInstructionsView> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/instructions/${args.scope}`,
    {
      method: 'PUT',
      headers: await authHeaders(),
      body: JSON.stringify({ instruction: args.instruction }),
    },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to save the instruction'));
  return normalizeInstructionsView(await res.json());
}

/** Drop one flow's instruction, so the flow runs on the platform's instruction alone. */
async function clearDeveloperInstruction(args: {
  id: string;
  scope: PromptScope;
}): Promise<DeveloperInstructionsView> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/instructions/${args.scope}`,
    { method: 'DELETE', headers: await authHeaders() },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to clear the instruction'));
  return normalizeInstructionsView(await res.json());
}

/**
 * Remove an older full replacement of one flow's platform instruction, so the platform's own runs
 * again. The answer is not read: the instructions view, which is what the tab shows, is fetched again.
 */
async function resetDeveloperPrompt(args: {
  id: string;
  scope: PromptScope;
}): Promise<void> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/prompts/${args.scope}`,
    { method: 'DELETE', headers: await authHeaders() },
  );
  if (!res.ok)
    throw new Error(
      await errorMessage(res, 'Failed to remove the old replacement'),
    );
}

/**
 * Coerce a prose response into a shape the screen can rely on, like the instructions view. A skill
 * this build does not offer stays as sent so the screen can name it; one that is not text reads as
 * the default.
 */
function normalizeProseView(body: unknown): DeveloperProseView {
  const view = isPlainObject(body) ? body : {};
  const stored = isPlainObject(view.instructions) ? view.instructions : {};
  const instructions = Object.fromEntries(
    PROSE_SCOPES.map((scope) => {
      const value = stored[scope.value];
      return [scope.value, typeof value === 'string' ? value : ''];
    }),
  ) as DeveloperProseInstructions;
  const skill = typeof view.skill === 'string' && view.skill;
  return { skill: skill || DEFAULT_PROSE_SKILL, instructions };
}

/** One developer's prose settings. A 404 is an older backend without the route, read as the defaults. */
async function fetchDeveloperProse(id: string): Promise<DeveloperProseView> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}/prose`, {
    headers: await authHeaders(),
  });
  if (res.status === 404) return normalizeProseView(undefined);
  if (!res.ok)
    throw new Error(
      await errorMessage(res, 'Failed to load the prose settings'),
    );
  return normalizeProseView(await res.json());
}

/** Pick the skill of the developer's first prose pass. The service refuses a skill it does not offer. */
async function saveDeveloperProseSkill(args: {
  id: string;
  skill: ProseSkill;
}): Promise<DeveloperProseView> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/prose/skill`,
    {
      method: 'PUT',
      headers: await authHeaders(),
      body: JSON.stringify({ skill: args.skill }),
    },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to save the prose skill'));
  return normalizeProseView(await res.json());
}

/** The address of one prose box. */
function proseInstructionUrl(id: string, scope: ProseScope): string {
  return `${ENDPOINT}/${encodeURIComponent(id)}/prose/instructions/${scope}`;
}

/** Store one prose box. A blank one clears it. */
async function saveDeveloperProseInstruction(args: {
  id: string;
  scope: ProseScope;
  instruction: string;
}): Promise<DeveloperProseView> {
  const res = await fetch(proseInstructionUrl(args.id, args.scope), {
    method: 'PUT',
    headers: await authHeaders(),
    body: JSON.stringify({ instruction: args.instruction }),
  });
  if (!res.ok)
    throw new Error(
      await errorMessage(res, 'Failed to save the prose instruction'),
    );
  return normalizeProseView(await res.json());
}

/** Drop one prose box. */
async function clearDeveloperProseInstruction(args: {
  id: string;
  scope: ProseScope;
}): Promise<DeveloperProseView> {
  const res = await fetch(proseInstructionUrl(args.id, args.scope), {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  if (!res.ok)
    throw new Error(
      await errorMessage(res, 'Failed to clear the prose instruction'),
    );
  return normalizeProseView(await res.json());
}

/** Write a new developer's imported prose after its create: one PUT for the skill, one per box. */
export async function importDeveloperProse(
  id: string,
  prose: ProseImport,
): Promise<void> {
  if (prose.proseSkill) {
    await saveDeveloperProseSkill({ id, skill: prose.proseSkill });
  }
  for (const scope of PROSE_SCOPES) {
    const instruction = prose.proseInstructions?.[scope.value];
    if (instruction) {
      await saveDeveloperProseInstruction({
        id,
        scope: scope.value,
        instruction,
      });
    }
  }
}

/**
 * Latched once a `GET /v1/developers/stats` 404 proves the backend doesn't support the endpoint, so the
 * 5s poll short-circuits instead of hammering the server. Only a 404 latches it; transient errors don't.
 */
let developerStatsUnsupported = false;

/** Shared empty stats result reused for the unsupported/404 case so React Query sees a stable reference. */
const EMPTY_STATS: DeveloperStat[] = [];

/**
 * The live stats row for every developer — feeds the global stats table. An older backend without this
 * endpoint answers 404; treat that as "unsupported", latch it in-module, and return an empty list so the
 * table stays hidden and the 5s poll stops hitting the server. Every other error still throws.
 */
async function fetchDeveloperStats(): Promise<DeveloperStat[]> {
  if (developerStatsUnsupported) return EMPTY_STATS;
  const res = await fetch(`${ENDPOINT}/stats`, {
    headers: await authHeaders(),
  });
  if (res.status === 404) {
    developerStatsUnsupported = true;
    return EMPTY_STATS;
  }
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to load developer stats'));
  }
  return (await res.json()) as DeveloperStat[];
}

/** The service has no developer with this id: a 404, which a page tells apart from a failed load. */
export class DeveloperNotFoundError extends Error {
  constructor() {
    super('Developer not found');
    this.name = 'DeveloperNotFoundError';
  }
}

async function fetchDeveloper(id: string): Promise<Developer> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}`, {
    headers: await authHeaders(),
  });
  if (res.status === 404) throw new DeveloperNotFoundError();
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to load developer'));
  return (await res.json()) as Developer;
}

async function fetchDeveloperByWorkspace(
  workspaceId: string,
): Promise<Developer | null> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(workspaceId)}`, {
    headers: await authHeaders(),
  });
  if (res.status === 404) return null;
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to load developer'));
  return (await res.json()) as Developer;
}

async function createDeveloper(
  input: DeveloperCreateInput,
): Promise<Developer> {
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(input),
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to create developer'));
  return (await res.json()) as Developer;
}

async function updateDeveloper(args: {
  id: string;
  patch: DeveloperUpdateInput;
}): Promise<Developer> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(args.id)}`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify(args.patch),
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to save developer'));
  return (await res.json()) as Developer;
}

async function deleteDeveloper(id: string): Promise<void> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  });
  if (!res.ok && res.status !== 204) {
    throw new Error(await errorMessage(res, 'Failed to delete developer'));
  }
}

/**
 * One page of a developer's runs, newest first. The cursor is the last row already held — its
 * `createdAt` plus its `id`, since runs can share a millisecond and the timestamp alone would repeat
 * or skip the rows on that boundary.
 */
async function fetchRuns(
  id: string,
  opts: { limit?: number; before?: string; beforeId?: string } = {},
): Promise<DeveloperRun[]> {
  const query = new URLSearchParams();
  if (opts.limit !== undefined) query.set('limit', String(opts.limit));
  if (opts.before) query.set('before', opts.before);
  if (opts.beforeId) query.set('beforeId', opts.beforeId);
  const suffix = query.size > 0 ? `?${query.toString()}` : '';
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(id)}/runs${suffix}`,
    { headers: await authHeaders() },
  );
  if (!res.ok) throw new Error(await errorMessage(res, 'Failed to load runs'));
  return (await res.json()) as DeveloperRun[];
}

/**
 * One run's stored execution log, oldest first. The service persists every activity line, so this
 * works for a run that finished long ago — the live SSE buffer only holds recent work. An older
 * service without the endpoint 404s, which reads as "no stored log" rather than an error.
 */
async function fetchRunLog(
  id: string,
  runId: string,
): Promise<ActivityEvent[]> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(id)}/runs/${encodeURIComponent(runId)}/log`,
    { headers: await authHeaders() },
  );
  if (res.status === 404) return [];
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to load the run log'));
  }
  const stored = (await res.json()) as (ActivityEvent & {
    extra?: Record<string, unknown>;
  })[];
  /** Flatten the stored `extra` payload back up, so a replayed line looks like a streamed one. */
  return stored.map(({ extra, ...event }) => ({
    ...(extra as Partial<ActivityEvent> | undefined),
    ...event,
    runId,
  }));
}

async function createRun(args: {
  id: string;
  question: string;
}): Promise<DeveloperRun> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(args.id)}/runs`, {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify({ question: args.question }),
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to start a run'));
  return (await res.json()) as DeveloperRun;
}

async function fetchSuggestions(id: string): Promise<Suggestion[]> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}/suggestions`, {
    headers: await authHeaders(),
  });
  /** A backend without the endpoint yet (404) or with it disabled (501) → no suggestions, not an error. */
  if (res.status === 404 || res.status === 501) return [];
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to load suggestions'));
  }
  return (await res.json()) as Suggestion[];
}

async function runSuggestion(args: {
  id: string;
  suggestion: Suggestion;
}): Promise<DeveloperRun> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/suggestions/run`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({
        source: args.suggestion.source,
        triggerRef: args.suggestion.triggerRef,
        question: args.suggestion.question,
        role: args.suggestion.role,
      }),
    },
  );
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to run the suggestion'));
  }
  return (await res.json()) as DeveloperRun;
}

async function resolveRun(args: {
  id: string;
  runId: string;
  decision: 'approve' | 'reject';
}): Promise<DeveloperRun> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/runs/${encodeURIComponent(args.runId)}/${args.decision}`,
    { method: 'POST', headers: await authHeaders() },
  );
  if (!res.ok) {
    throw new Error(
      await errorMessage(res, `Failed to ${args.decision} the reply`),
    );
  }
  return (await res.json()) as DeveloperRun;
}

async function retryRun(args: {
  id: string;
  runId: string;
}): Promise<DeveloperRun> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/runs/${encodeURIComponent(args.runId)}/retry`,
    { method: 'POST', headers: await authHeaders() },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to retry the run'));
  return (await res.json()) as DeveloperRun;
}

async function resetDeveloper(id: string): Promise<void> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}/reset`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to reset the developer'));
  }
}

/** Stop one active run. The service answers 404 when that run is no longer active. */
async function stopRun(args: {
  id: string;
  runId: string;
}): Promise<{ stopped: boolean }> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/runs/${encodeURIComponent(args.runId)}/stop`,
    { method: 'POST', headers: await authHeaders() },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to stop the run'));
  return (await res.json()) as { stopped: boolean };
}

/**
 * Every run of every developer in a workspace the caller belongs to, inside one window. `scope=member`
 * keeps a fleet-wide reader (admin, `developer:read`) to their own workspaces. A 404 means the backend
 * predates the endpoint, which is not an error the user can act on — it comes back as `unsupported`
 * so the feed can say so, rather than as an empty week or a failure. Every other bad status still
 * throws. Nothing is latched in the module: the query stops polling on `unsupported` instead.
 */
async function fetchActivity(
  from: string,
  to: string,
): Promise<ActivityWindow> {
  const query = new URLSearchParams({ from, to, scope: 'member' });
  const res = await fetch(`${ENDPOINT}/runs?${query.toString()}`, {
    headers: await authHeaders(),
  });
  if (res.status === 404) {
    return { from, to, truncated: false, runs: [], unsupported: true };
  }
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to load activity'));
  }
  const window = (await res.json()) as Partial<
    Omit<ActivityWindow, 'unsupported'>
  >;
  /** Every field is defaulted: the body is cast, not validated, and a missing `truncated` must not read as "complete". */
  return {
    from: window.from ?? from,
    to: window.to ?? to,
    truncated: window.truncated ?? false,
    runs: window.runs ?? [],
    unsupported: false,
  };
}

/**
 * The developer's open sessions — the runs it is still working or parked on. These are what hold it at
 * Running or Waiting, so this is the list to read when a developer looks stuck.
 */
async function fetchSessions(id: string): Promise<DeveloperRun[]> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}/sessions`, {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to load open sessions'));
  }
  return (await res.json()) as DeveloperRun[];
}

async function closeSession(args: {
  id: string;
  runId: string;
}): Promise<{ stopped: boolean }> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/sessions/${encodeURIComponent(args.runId)}`,
    { method: 'DELETE', headers: await authHeaders() },
  );
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to close the session'));
  }
  return (await res.json()) as { stopped: boolean };
}

/** Close every open session so the developer goes back to idle. */
async function setDeveloperIdle(id: string): Promise<{ stopped: number }> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}/idle`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (!res.ok) {
    throw new Error(
      await errorMessage(res, 'Failed to set the developer idle'),
    );
  }
  return (await res.json()) as { stopped: number };
}

/**
 * Acknowledge the developer's newest failed run so its status goes from error back to idle. The run
 * itself stays failed in history.
 */
async function clearDeveloperError(
  id: string,
): Promise<{ cleared: boolean; status: DeveloperStatus }> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}/clear-error`, {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to clear the error'));
  }
  return (await res.json()) as { cleared: boolean; status: DeveloperStatus };
}

async function exportSettings(id: string): Promise<DeveloperExport> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}/export`, {
    headers: await authHeaders(),
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to export settings'));
  return (await res.json()) as DeveloperExport;
}

async function fetchConnectionStatus(id: string): Promise<ConnectionCheckView> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(id)}/connection-status`,
    { headers: await authHeaders() },
  );
  if (!res.ok) {
    throw new Error(
      await errorMessage(res, 'Failed to load the connection status'),
    );
  }
  return (await res.json()) as ConnectionCheckView;
}

/** Ask for a background check. A check already running is returned as is; nothing new starts. */
async function startConnectionCheck(id: string): Promise<ConnectionCheckView> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(id)}/connection-status/check`,
    { method: 'POST', headers: await authHeaders() },
  );
  if (!res.ok) {
    throw new Error(
      await errorMessage(res, 'Failed to start the connection check'),
    );
  }
  return (await res.json()) as ConnectionCheckView;
}

async function fetchJiraProjects(id: string): Promise<JiraProject[]> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(id)}/jira/projects`,
    {
      headers: await authHeaders(),
    },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to load Jira projects'));
  return (await res.json()) as JiraProject[];
}

async function fetchJiraProjectStatuses(
  id: string,
  projectKey: string,
): Promise<JiraIssueTypeStatuses[]> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(id)}/jira/projects/${encodeURIComponent(projectKey)}/statuses`,
    { headers: await authHeaders() },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to load Jira statuses'));
  return (await res.json()) as JiraIssueTypeStatuses[];
}

/**
 * A repo's remote branches, for the branch picklists. Fails **soft**: the endpoint is only there on a
 * new-enough backend, the repo may not be saved yet, and its credential may be revoked — in every one
 * of those cases the caller wants a free-text branch input, not an error dialog over a field the user
 * can simply type. So any failure resolves to an empty list.
 */
async function fetchRepoBranches(
  id: string,
  repoId: string,
): Promise<string[]> {
  try {
    const res = await fetch(
      `${ENDPOINT}/${encodeURIComponent(id)}/repos/${encodeURIComponent(repoId)}/branches`,
      { headers: await authHeaders() },
    );
    if (!res.ok) return [];
    return ((await res.json()) as { branches?: string[] }).branches ?? [];
  } catch {
    return [];
  }
}

/**
 * Older backends don't expose the project-memory endpoint, so a 404 degrades gracefully to empty
 * content rather than surfacing an error. Any other non-OK response still throws.
 */
async function fetchProjectMemory(
  id: string,
  repoId?: string,
): Promise<string> {
  const query = repoId ? `?repoId=${encodeURIComponent(repoId)}` : '';
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(id)}/project-memory${query}`,
    { headers: await authHeaders() },
  );
  if (res.status === 404) return '';
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to load project memory'));
  }
  return ((await res.json()) as { content?: string }).content ?? '';
}

async function saveProjectMemory(args: {
  id: string;
  content: string;
  repoId?: string;
}): Promise<SaveRepoDocResult> {
  const query = args.repoId ? `?repoId=${encodeURIComponent(args.repoId)}` : '';
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/project-memory${query}`,
    {
      method: 'PUT',
      headers: await authHeaders(),
      body: JSON.stringify({ content: args.content }),
    },
  );
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to save project memory'));
  }
  return (await res.json()) as SaveRepoDocResult;
}

/** One decoded SSE block: its `event` name (default `message`) and joined `data` payload. */
export interface DeveloperStreamEvent {
  event: string;
  data: string;
}

/**
 * Parse a raw SSE body (`event:`/`data:` blocks) into `{ event, data }`, tolerating CRLF framing and
 * multi-byte chars split across chunks. Shared by the activity and status readers; cancels the body
 * when the consumer abandons the generator so the connection isn't left open.
 */
async function* parseSseBlocks(
  body: ReadableStream<Uint8Array>,
): AsyncGenerator<DeveloperStreamEvent> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      const blocks = buffer.split(/\r?\n\r?\n/);
      buffer = done ? '' : (blocks.pop() ?? '');
      for (const block of blocks) {
        let event = 'message';
        const dataLines: string[] = [];
        for (const line of block.split(/\r?\n/)) {
          if (line.startsWith('event:')) event = line.slice(6).trim();
          else if (line.startsWith('data:'))
            dataLines.push(line.slice(5).trim());
        }
        const data = dataLines.join('\n');
        if (data) yield { event, data };
      }
      if (done) break;
    }
  } finally {
    await reader.cancel().catch(() => undefined);
  }
}

/**
 * Consume the developer's SSE stream and yield every `{ event, data }` block, whatever the event
 * name. Uses fetch + Bearer (not EventSource) so the platform JWT rides along. Yields nothing on a
 * non-ok response so a caller (e.g. the status stream) can treat it as "no events".
 */
export async function* streamDeveloperEvents(
  id: string,
  signal?: AbortSignal,
): AsyncGenerator<DeveloperStreamEvent> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(id)}/activity/stream`,
    { method: 'GET', headers: await authHeaders(), signal },
  );
  if (!res.ok || !res.body) return;
  yield* parseSseBlocks(res.body);
}

/**
 * Consume the developer's activity SSE stream and yield each parsed `activity` event, dropping any
 * other event type (e.g. `status`). Surfaces an error event on a non-ok response so the log can show
 * idle ≠ disconnected.
 */
export async function* streamActivity(
  id: string,
  signal?: AbortSignal,
): AsyncGenerator<ActivityEvent> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(id)}/activity/stream`,
    { method: 'GET', headers: await authHeaders(), signal },
  );
  if (!res.ok) {
    yield {
      level: 'error',
      message: `Activity stream unavailable (${res.status}).`,
    };
    return;
  }
  if (!res.body) return;
  for await (const { event, data } of parseSseBlocks(res.body)) {
    if (event !== 'activity') continue;
    try {
      yield JSON.parse(data) as ActivityEvent;
    } catch {
      continue;
    }
  }
}

/** True while a developer is mid-run — status badges poll faster so they don't sit stale. */
function isActiveStatus(status: DeveloperStatus | undefined): boolean {
  return status === 'running' || status === 'waiting';
}

export function developersQueryOptions() {
  return queryOptions({
    queryKey: ['developers'],
    queryFn: fetchDevelopers,
    staleTime: 30_000,
    refetchInterval: (query) =>
      (query.state.data ?? []).some((dev) => isActiveStatus(dev.status))
        ? 5_000
        : 60_000,
  });
}

/**
 * The **platform-default** per-request-type flows for one kind of developer. Still needed alongside a
 * developer's own flows: it is what Reset restores, and the step count the confirm names before
 * dropping an override. The type is part of the key, or a second developer type would be answered from
 * the first one's cache entry; it defaults to `salesforce` exactly as the endpoint does, so a developer
 * whose type has not loaded reads what that endpoint has always served.
 */
export function flowsQueryOptions(type: DeveloperType = 'salesforce') {
  return queryOptions({
    queryKey: ['developers', 'flows', type],
    queryFn: () => fetchFlows(type),
    staleTime: 5 * 60_000,
  });
}

/**
 * One developer's resolved flows. Deliberately not polled: the Steps editor holds an unsaved draft, and
 * a background refetch landing mid-edit would be at best noise.
 */
export function developerFlowsQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: ['developers', id, 'flows'],
    queryFn: () => fetchDeveloperFlows(id!),
    enabled: id !== undefined,
    staleTime: 30_000,
  });
}

/**
 * One developer's own instructions. Deliberately not polled, like the flows: the Instructions editor
 * holds an unsaved draft, and a background refetch landing mid-edit would be at best noise.
 */
export function developerInstructionsQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: ['developers', id, 'instructions'],
    queryFn: () => fetchDeveloperInstructions(id!),
    enabled: id !== undefined,
    staleTime: 30_000,
  });
}

/** One developer's prose settings. Not polled, like the instructions, since the boxes hold drafts. */
export function developerProseQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: ['developers', id, 'prose'],
    queryFn: () => fetchDeveloperProse(id!),
    enabled: id !== undefined,
    staleTime: 30_000,
  });
}

/** The live stats for every developer — polled every 5s like the developers list. */
export function developerStatsQueryOptions() {
  return queryOptions({
    queryKey: ['developers', 'stats'],
    queryFn: fetchDeveloperStats,
    staleTime: 5_000,
    refetchInterval: 5_000,
  });
}

/** The cache key of one developer's connection status, shared by the read, a started check and a save. */
export function connectionStatusKey(id: string): string[] {
  return ['developer-connection-status', id];
}

/** The developer's last connection check, polled every 3 seconds while one is running and not otherwise. */
export function connectionStatusQueryOptions(id: string) {
  return queryOptions({
    queryKey: connectionStatusKey(id),
    queryFn: () => fetchConnectionStatus(id),
    refetchInterval: (query) =>
      query.state.data?.state === 'running' ? 3_000 : false,
  });
}

/** Read the developer's connection status; see `connectionStatusQueryOptions`. */
export function useConnectionStatus(
  id: string,
): UseQueryResult<ConnectionCheckView> {
  return useQuery(connectionStatusQueryOptions(id));
}

export function developerQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: ['developers', id],
    queryFn: () => fetchDeveloper(id as string),
    enabled: id !== undefined,
    staleTime: 30_000,
    refetchInterval: (query) =>
      isActiveStatus(query.state.data?.status) ? 3_000 : 60_000,
  });
}

/**
 * The cache key for "which developer does this workspace have". Exported because the workspace
 * domain toggle has to invalidate it: enabling the domain **provisions** the developer, so the
 * cached answer from before the toggle is wrong the moment it succeeds.
 */
export function developerByWorkspaceKey(workspaceId: string): string[] {
  return ['developers', 'by-workspace', workspaceId];
}

/**
 * The developer resolved by workspace id (developer.id == workspace.id). `enabled` gates the fetch to
 * workspaces whose Developer domain is on. A distinct query key from `developerQueryOptions` so a null
 * (unprovisioned) result doesn't collide with the developers-page cache.
 *
 * **`staleTime: 0`, unlike every other query in this file.** This answer gates a decision that cannot
 * be revised — whether the Developer tab offers the developer's kind — so serving it from a cache is
 * the wrong default here: a `null` read half a minute ago is still successful and no longer fetching,
 * which is precisely the shape the tab reads as a settled "there is no developer yet". Zero means the
 * question is re-asked on mount and on focus, so a cached answer is never treated as settled without a
 * refetch in flight for `isFetching` to cover.
 *
 * It does not close the race, and nothing on this side can: another owner can provision between the
 * read and the click. What closes it is the service — `ensureForWorkspace` is 1:1 with the workspace
 * id, so an enable that meets an existing row keeps the type that row was born with and ignores the
 * one sent. The interface can be wrong about *offering* the choice; it cannot be wrong about what the
 * choice does.
 */
export function developerByWorkspaceQueryOptions(
  workspaceId: string,
  enabled: boolean,
) {
  return queryOptions({
    queryKey: developerByWorkspaceKey(workspaceId),
    queryFn: () => fetchDeveloperByWorkspace(workspaceId),
    enabled,
    staleTime: 0,
  });
}
export function runsQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: ['developers', id, 'runs'],
    queryFn: () => fetchRuns(id as string),
    enabled: id !== undefined,
    staleTime: 10_000,
  });
}

/** How many runs one History page loads. */
const RUNS_PAGE_SIZE = 20;

/**
 * History paged back through the whole retained run history. A short page means there is nothing
 * older, so `getNextPageParam` returns undefined and the "Load more" button disappears.
 */
export function runsInfiniteQueryOptions(id: string | undefined) {
  return infiniteQueryOptions({
    queryKey: ['developers', id, 'runs', 'infinite'],
    queryFn: ({ pageParam }) =>
      fetchRuns(id as string, { limit: RUNS_PAGE_SIZE, ...pageParam }),
    initialPageParam: {} as { before?: string; beforeId?: string },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < RUNS_PAGE_SIZE) return undefined;
      const last = lastPage[lastPage.length - 1];
      if (!last) return undefined;
      /**
       * An older service ignores the cursor and answers with the same newest page every time, which
       * would page forever, re-appending the same runs. Seeing the cursor row again means the response
       * did not move, so stop here.
       */
      const repeated = allPages
        .slice(0, -1)
        .some((page) => page.some((run) => run.id === last.id));
      if (repeated) return undefined;
      return { before: last.createdAt, beforeId: last.id };
    },
    enabled: id !== undefined,
    staleTime: 10_000,
    /** A row still working has to flip to its outcome without a reload; a finished list is history. */
    refetchInterval: (query) =>
      (query.state.data?.pages.flat() ?? []).some(
        (run) => !TERMINAL_RUN_STATUSES.includes(run.status),
      )
        ? 10_000
        : false,
  });
}

/**
 * One window of cross-developer activity, keyed by the window itself so switching range re-queries and
 * switching back is instant. Polled fast only while something is live; a backend without the endpoint
 * is not polled at all — it will not grow one while the page is open.
 */
export function activityQueryOptions(from: string, to: string) {
  return queryOptions({
    queryKey: ['developers', 'activity', from, to],
    queryFn: () => fetchActivity(from, to),
    staleTime: 10_000,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (data?.unsupported) return false;
      return (data?.runs ?? []).some(
        (run) => !TERMINAL_RUN_STATUSES.includes(run.status),
      )
        ? 10_000
        : 60_000;
    },
  });
}

/** One run's stored execution log — what it actually did, readable after the fact. */
export function runLogQueryOptions(
  id: string | undefined,
  runId: string | undefined,
) {
  return queryOptions({
    queryKey: ['developers', id, 'runs', runId, 'log'],
    queryFn: () => fetchRunLog(id as string, runId as string),
    enabled: id !== undefined && runId !== undefined,
    staleTime: 10_000,
  });
}

/**
 * A developer's open sessions. A session opens and closes with no action on this page — the sweep and
 * the developer itself both end them — so the list has to poll, not merely go stale: the app disables
 * `refetchOnWindowFocus`, so `staleTime` alone would leave a tab open on a list that never refreshes
 * and offer to close work that finished long ago.
 */
export function sessionsQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: ['developers', id, 'sessions'],
    queryFn: () => fetchSessions(id as string),
    enabled: id !== undefined,
    staleTime: 10_000,
    refetchInterval: 10_000,
  });
}

export function suggestionsQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: ['developers', id, 'suggestions'],
    queryFn: () => fetchSuggestions(id as string),
    enabled: id !== undefined,
    staleTime: 60_000,
  });
}

/** Suggested history actions for a developer (recent pre-creation work it can run on demand). */
export function useSuggestions(
  id: string | undefined,
): UseQueryResult<Suggestion[], Error> {
  return useQuery(suggestionsQueryOptions(id));
}

/** Run a chosen suggested history action; refreshes runs + suggestions (the run'd item drops off). */
export function useRunSuggestion(): UseMutationResult<
  DeveloperRun,
  Error,
  { id: string; suggestion: Suggestion }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: runSuggestion,
    onSuccess: (run) => {
      void qc.invalidateQueries({
        queryKey: ['developers', run.developerId, 'runs'],
      });
      void qc.invalidateQueries({
        queryKey: ['developers', run.developerId, 'suggestions'],
      });
      void qc.invalidateQueries({ queryKey: ['developers', run.developerId] });
    },
  });
}

export function useCreateDeveloper(): UseMutationResult<
  Developer,
  Error,
  DeveloperCreateInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createDeveloper,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['developers'] });
    },
  });
}

export function useUpdateDeveloper(): UseMutationResult<
  Developer,
  Error,
  { id: string; patch: DeveloperUpdateInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateDeveloper,
    onSuccess: (dev, variables) => {
      /**
       * Write the saved developer straight into the cache, not just invalidate. The settings form
       * re-seeds from this the moment a save succeeds; leaving it to an async refetch would seed it
       * from pre-save data, snapping edits back and dropping a repo added in this session.
       */
      qc.setQueryData(['developers', dev.id], dev);
      void qc.invalidateQueries({ queryKey: ['developers'] });
      void qc.invalidateQueries({
        queryKey: connectionStatusKey(variables.id),
      });
    },
  });
}

export function useDeleteDeveloper(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteDeveloper,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['developers'] });
    },
  });
}

export function useCreateRun(): UseMutationResult<
  DeveloperRun,
  Error,
  { id: string; question: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createRun,
    onSuccess: (run) => {
      void qc.invalidateQueries({
        queryKey: ['developers', run.developerId, 'runs'],
      });
      void qc.invalidateQueries({ queryKey: ['developers'] });
      void qc.invalidateQueries({ queryKey: ['developers', run.developerId] });
    },
  });
}

/** Retry a failed run — requeues it so the agent works it again from scratch. */
export function useRetryRun(): UseMutationResult<
  DeveloperRun,
  Error,
  { id: string; runId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: retryRun,
    /** Invalidate on the developer id we called with (a variable), not one read back off the
     * response — a partial/reshaped payload can't then silently skip the refetch. */
    onSuccess: (_run, variables) => {
      void qc.invalidateQueries({
        queryKey: ['developers', variables.id, 'runs'],
      });
      void qc.invalidateQueries({ queryKey: ['developers'] });
      void qc.invalidateQueries({ queryKey: ['developers', variables.id] });
    },
  });
}

/** Reset a developer to a clean state (clear runs, cursors, and the activity log) — for debugging. */
export function useResetDeveloper(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: resetDeveloper,
    onSuccess: (_result, id) => {
      void qc.invalidateQueries({ queryKey: ['developers', id, 'runs'] });
      void qc.invalidateQueries({ queryKey: ['developers'] });
      void qc.invalidateQueries({ queryKey: ['developers', id] });
    },
  });
}

/**
 * Stop one active run. Everything derived from the developer's live runs moves — its status badge,
 * the session list, the activity feed and the global window — so all of them are invalidated.
 */
export function useStopRun(): UseMutationResult<
  { stopped: boolean },
  Error,
  { id: string; runId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: stopRun,
    onSuccess: (_result, args) => {
      void qc.invalidateQueries({ queryKey: ['developers', args.id, 'runs'] });
      void qc.invalidateQueries({
        queryKey: ['developers', args.id, 'sessions'],
      });
      void qc.invalidateQueries({ queryKey: ['developers', 'activity'] });
      void qc.invalidateQueries({ queryKey: ['developers', args.id] });
      void qc.invalidateQueries({ queryKey: ['developers'] });
    },
  });
}

/**
 * Close one open session. Everything derived from the developer's live runs moves — its status badge,
 * the session list, and History, which gains the closed run — so all three are invalidated.
 */
export function useCloseSession(): UseMutationResult<
  { stopped: boolean },
  Error,
  { id: string; runId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: closeSession,
    onSuccess: (_result, args) => {
      void qc.invalidateQueries({
        queryKey: ['developers', args.id, 'sessions'],
      });
      void qc.invalidateQueries({ queryKey: ['developers', args.id, 'runs'] });
      void qc.invalidateQueries({ queryKey: ['developers'] });
      void qc.invalidateQueries({ queryKey: ['developers', args.id] });
    },
  });
}

/** Send a developer back to idle by closing every session it holds. */
export function useSetDeveloperIdle(): UseMutationResult<
  { stopped: number },
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: setDeveloperIdle,
    onSuccess: (_result, id) => {
      void qc.invalidateQueries({ queryKey: ['developers', id, 'sessions'] });
      void qc.invalidateQueries({ queryKey: ['developers', id, 'runs'] });
      void qc.invalidateQueries({ queryKey: ['developers'] });
      void qc.invalidateQueries({ queryKey: ['developers', id] });
    },
  });
}

/**
 * Clear a developer's error. The answered status goes straight into the cached developer, so the badge
 * flips before the refetch lands.
 */
export function useClearDeveloperError(): UseMutationResult<
  { cleared: boolean; status: DeveloperStatus },
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: clearDeveloperError,
    onSuccess: (result, id) => {
      qc.setQueryData(developerQueryOptions(id).queryKey, (prev) =>
        prev ? { ...prev, status: result.status } : prev,
      );
      void qc.invalidateQueries({ queryKey: ['developers', id, 'sessions'] });
      void qc.invalidateQueries({ queryKey: ['developers', id, 'runs'] });
      void qc.invalidateQueries({ queryKey: ['developers'] });
      void qc.invalidateQueries({ queryKey: ['developers', id] });
    },
  });
}

/** Fetch a developer's full portable config, secrets included, for download. */
export function fetchDeveloperExport(id: string): Promise<DeveloperExport> {
  return exportSettings(id);
}

/** Start a background connection check and show its answer at once, without waiting for the next read. */
export function useStartConnectionCheck(): UseMutationResult<
  ConnectionCheckView,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: startConnectionCheck,
    /**
     * A status read already in flight answers from before the check started; landing after the
     * answer below, it would hide the running check and stop the polling that follows it.
     */
    onMutate: (id) => qc.cancelQueries({ queryKey: connectionStatusKey(id) }),
    onSuccess: (view, id) => {
      qc.setQueryData(connectionStatusKey(id), view);
    },
  });
}

/**
 * The Confluence spaces the developer can see — the suggestions behind its space-scope input.
 *
 * Answers `[]` on any failure rather than throwing: the scope is optional, so an unconfigured or
 * unreachable Confluence must leave the field usable as free text instead of showing an error over
 * something somebody can simply type. Connection status is where a broken credential is reported.
 */
async function fetchConfluenceSpaces(id: string): Promise<ConfluenceSpace[]> {
  /**
   * The **whole** request is inside the catch, not only the status check. A rejected fetch, an
   * `authHeaders()` that throws, or a body that is not JSON would otherwise reject the query and put
   * it into React Query's retry-and-error path — which is the opposite of what this is for, and what
   * `fetchRepoBranches` above already avoids.
   */
  try {
    const res = await fetch(
      `${ENDPOINT}/${encodeURIComponent(id)}/confluence/spaces`,
      { headers: await authHeaders() },
    );
    if (!res.ok) return [];
    const body = (await res.json()) as { spaces?: unknown };
    return confluenceSpacesOf(body.spaces);
  } catch {
    return [];
  }
}

/**
 * The spaces in a response body, or none.
 *
 * A cast is not a check: a 200 carrying `{spaces: [null]}` satisfied the type and then threw at
 * `space.key` while the datalist rendered, which is the one thing this function promises cannot
 * happen. A `name` that is not a string is dropped rather than rendered, since React refuses an
 * object as a child.
 */
function confluenceSpacesOf(value: unknown): ConfluenceSpace[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((entry) => {
    if (typeof entry !== 'object' || entry === null) return [];
    const { key, name } = entry as { key?: unknown; name?: unknown };
    if (typeof key !== 'string' || key.trim() === '') return [];
    return [typeof name === 'string' ? { key, name } : { key }];
  });
}

export function jiraProjectsQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: ['developers', id, 'jira', 'projects'],
    queryFn: () => fetchJiraProjects(id!),
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}

export function jiraProjectStatusesQueryOptions(
  id: string | undefined,
  projectKey: string | undefined,
  enabled = true,
) {
  return queryOptions({
    queryKey: ['developers', id, 'jira', 'projects', projectKey, 'statuses'],
    queryFn: () => fetchJiraProjectStatuses(id!, projectKey!),
    enabled: enabled && !!id && !!projectKey,
    staleTime: 5 * 60_000,
  });
}

/**
 * The developer's Confluence spaces. Exported as query options as well as a hook, like the Jira and
 * branch lookups beside it — which is also what lets the fail-soft promise be asserted at this level,
 * where the form tests cannot reach it (they render without a developer id, so the query never runs).
 */
export function confluenceSpacesQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: ['developers', id, 'confluence', 'spaces'],
    queryFn: () => fetchConfluenceSpaces(id!),
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}

/** The developer's Confluence spaces — the suggestions behind the space-scope input. */
export function useConfluenceSpaces(
  id: string | undefined,
): UseQueryResult<ConfluenceSpace[], Error> {
  return useQuery(confluenceSpacesQueryOptions(id));
}

/** The developer's Jira projects (owner-scoped) — feeds the space project picker. */
export function useJiraProjects(
  id: string | undefined,
): UseQueryResult<JiraProject[], Error> {
  return useQuery(jiraProjectsQueryOptions(id));
}

/** A Jira project's issue types + statuses — feeds the status-trigger editor; off until a project is set. */
export function useJiraProjectStatuses(
  id: string | undefined,
  projectKey: string | undefined,
  enabled = true,
): UseQueryResult<JiraIssueTypeStatuses[], Error> {
  return useQuery(jiraProjectStatusesQueryOptions(id, projectKey, enabled));
}

/**
 * A repo's remote branches, for the branch picklists. Only queried once both ids are known — on the
 * create form the developer does not exist yet and the repo has no id, so the caller falls back to a
 * free-text branch input. `fetchRepoBranches` never throws, so an empty result is the single "no
 * picklist" signal whatever the reason.
 */
export function repoBranchesQueryOptions(
  id: string | undefined,
  repoId: string | undefined,
) {
  return queryOptions({
    queryKey: ['developers', id, 'repos', repoId, 'branches'],
    queryFn: () => fetchRepoBranches(id as string, repoId as string),
    enabled: id !== undefined && repoId !== undefined,
    /** Branches change rarely and a form re-renders often; five minutes avoids a call per keystroke. */
    staleTime: 300_000,
  });
}

/** One working repo's project memory for editing (loaded lazily when the tab opens). */
export function projectMemoryQueryOptions(
  id: string | undefined,
  repoId?: string,
) {
  return queryOptions({
    queryKey: ['developers', id, 'project-memory', repoId ?? null],
    queryFn: () => fetchProjectMemory(id as string, repoId),
    enabled: id !== undefined,
    staleTime: 30_000,
  });
}

/** Save the project memory (opens + auto-merges a PR against the default branch). */
export function useSaveProjectMemory(): UseMutationResult<
  SaveRepoDocResult,
  Error,
  { id: string; content: string; repoId?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveProjectMemory,
    onSuccess: (_result, args) => {
      void qc.invalidateQueries({
        queryKey: ['developers', args.id, 'project-memory'],
      });
    },
  });
}

/**
 * Save one request type's flow. Writes the server's response straight into the flows cache so the
 * editor snaps to the stored version — including any step id the server had to mint — then invalidates
 * only that key. Nothing on the `Developer` itself reflects an override, so nothing else needs refetching.
 */
export function useSaveDeveloperFlow(): UseMutationResult<
  DeveloperFlowsView,
  Error,
  { id: string; requestType: RequestType; steps: FlowStepInput[] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveDeveloperFlow,
    onSuccess: (view, args) => {
      qc.setQueryData(['developers', args.id, 'flows'], view);
      void qc.invalidateQueries({
        queryKey: ['developers', args.id, 'flows'],
      });
    },
  });
}

/** Reset one request type's flow to the platform default. */
export function useResetDeveloperFlow(): UseMutationResult<
  DeveloperFlowsView,
  Error,
  { id: string; requestType: RequestType }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: resetDeveloperFlow,
    onSuccess: (view, args) => {
      qc.setQueryData(['developers', args.id, 'flows'], view);
      void qc.invalidateQueries({
        queryKey: ['developers', args.id, 'flows'],
      });
    },
  });
}

/**
 * Write a returned instructions view into its key, the way the flow mutations do. Nothing on the
 * `Developer` itself reflects an instruction, so nothing else needs refetching.
 */
function storeInstructionsView(
  qc: QueryClient,
  id: string,
  view: DeveloperInstructionsView,
): void {
  qc.setQueryData(['developers', id, 'instructions'], view);
  void qc.invalidateQueries({ queryKey: ['developers', id, 'instructions'] });
}

/**
 * The mutation keys of the writes to an instruction. A write lives in the mutation cache, not in the
 * editor that started it, so an editor mounted again while a write runs can still see it.
 */
const instructionWriteKeys = {
  all: ['developer-instruction-write'] as const,
  save: ['developer-instruction-write', 'save'] as const,
  clear: ['developer-instruction-write', 'clear'] as const,
  removeReplacement: [
    'developer-instruction-write',
    'remove-replacement',
  ] as const,
};

/** The mutation key of a prose skill save, so a picker mounted again still sees one that runs. */
const proseSkillWriteKey = ['developer-prose-skill-write'] as const;

/** The mutation keys of the writes to a prose box, apart from the instructions that share its scopes. */
const proseWriteKeys = {
  all: ['developer-prose-write'] as const,
  save: ['developer-prose-write', 'save'] as const,
  clear: ['developer-prose-write', 'clear'] as const,
};

/** Whether a mutation writes the instruction of `id` for `scope`, read from what it was called with. */
function writesInstruction(
  mutation: Mutation<unknown, Error, unknown>,
  id: string,
  scope: PromptScope,
): boolean {
  const target = mutation.state.variables as
    { id?: unknown; scope?: unknown } | undefined;
  return target?.id === id && target.scope === scope;
}

/**
 * Whether a write to one developer's box for one scope is still running under `keys`, and whether it
 * is a save. It counts the writes in the mutation cache, so it holds across an editor that unmounts
 * and mounts again, and a later write can never race one still in flight.
 */
function useWritesPending(
  keys: { all: readonly string[]; save: readonly string[] },
  id: string,
  scope: PromptScope,
): { busy: boolean; saving: boolean } {
  const predicate = (mutation: Mutation<unknown, Error, unknown>) =>
    writesInstruction(mutation, id, scope);
  return {
    busy: useIsMutating({ mutationKey: keys.all, predicate }) > 0,
    saving: useIsMutating({ mutationKey: keys.save, predicate }) > 0,
  };
}

/** Whether a write to one developer's instruction for one scope is still running, and whether it is a save. */
export function useInstructionWritesPending(
  id: string,
  scope: PromptScope,
): { busy: boolean; saving: boolean } {
  return useWritesPending(instructionWriteKeys, id, scope);
}

/** Whether a write to one developer's prose box for one scope is still running, and whether it is a save. */
export function useProseWritesPending(
  id: string,
  scope: ProseScope,
): { busy: boolean; saving: boolean } {
  return useWritesPending(proseWriteKeys, id, scope);
}

/** Save one flow's instruction; a blank one clears it. */
export function useSaveDeveloperInstruction(): UseMutationResult<
  DeveloperInstructionsView,
  Error,
  { id: string; scope: PromptScope; instruction: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: instructionWriteKeys.save,
    mutationFn: saveDeveloperInstruction,
    onSuccess: (view, args) => storeInstructionsView(qc, args.id, view),
  });
}

/** Clear one flow's instruction. */
export function useClearDeveloperInstruction(): UseMutationResult<
  DeveloperInstructionsView,
  Error,
  { id: string; scope: PromptScope }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: instructionWriteKeys.clear,
    mutationFn: clearDeveloperInstruction,
    onSuccess: (view, args) => storeInstructionsView(qc, args.id, view),
  });
}

/**
 * Remove an older full replacement of one flow's platform instruction. The instructions view names
 * the flows that still have one, so it is read again.
 */
export function useResetDeveloperPrompt(): UseMutationResult<
  void,
  Error,
  { id: string; scope: PromptScope }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: instructionWriteKeys.removeReplacement,
    mutationFn: resetDeveloperPrompt,
    onSuccess: (_result, args) => {
      void qc.invalidateQueries({
        queryKey: ['developers', args.id, 'instructions'],
      });
    },
  });
}

/** Write a returned prose view into its key, the way the instruction writes do. */
function storeProseView(
  qc: QueryClient,
  id: string,
  view: DeveloperProseView,
): void {
  qc.setQueryData(['developers', id, 'prose'], view);
  void qc.invalidateQueries({ queryKey: ['developers', id, 'prose'] });
}

/** Pick the skill of the developer's first prose pass. */
export function useSaveDeveloperProseSkill(): UseMutationResult<
  DeveloperProseView,
  Error,
  { id: string; skill: ProseSkill }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: proseSkillWriteKey,
    mutationFn: saveDeveloperProseSkill,
    onSuccess: (view, args) => storeProseView(qc, args.id, view),
  });
}

/**
 * The skill a save still running for developer `id` writes, or undefined when none runs. It reads the
 * mutation cache, so a picker mounted again during a save sees it, and never starts a second one that
 * could land first and be overwritten.
 */
export function usePendingProseSkill(id: string): ProseSkill | undefined {
  const pendingSkills = useMutationState({
    filters: {
      mutationKey: proseSkillWriteKey,
      status: 'pending',
      predicate: (mutation) =>
        (mutation.state.variables as { id?: unknown } | undefined)?.id === id,
    },
    select: (mutation) =>
      (mutation.state.variables as { skill: ProseSkill }).skill,
  });
  return pendingSkills.at(-1);
}

/** Save one prose box; a blank one clears it. */
export function useSaveDeveloperProseInstruction(): UseMutationResult<
  DeveloperProseView,
  Error,
  { id: string; scope: ProseScope; instruction: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: proseWriteKeys.save,
    mutationFn: saveDeveloperProseInstruction,
    onSuccess: (view, args) => storeProseView(qc, args.id, view),
  });
}

/** Clear one prose box. */
export function useClearDeveloperProseInstruction(): UseMutationResult<
  DeveloperProseView,
  Error,
  { id: string; scope: ProseScope }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: proseWriteKeys.clear,
    mutationFn: clearDeveloperProseInstruction,
    onSuccess: (view, args) => storeProseView(qc, args.id, view),
  });
}

/** Approve or reject a parked reply (mode=ask). */
export function useResolveRun(): UseMutationResult<
  DeveloperRun,
  Error,
  { id: string; runId: string; decision: 'approve' | 'reject' }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: resolveRun,
    onSuccess: (run) => {
      void qc.invalidateQueries({
        queryKey: ['developers', run.developerId, 'runs'],
      });
      void qc.invalidateQueries({ queryKey: ['developers'] });
      void qc.invalidateQueries({ queryKey: ['developers', run.developerId] });
    },
  });
}

/**
 * Subscribe to the developer's SSE `status` events and push each into the TanStack cache — both the
 * single-developer entry and the matching row in the list — so the status icon updates in real time.
 * The existing `refetchInterval` polling stays as a fallback; this just makes the change immediate.
 * The stream is aborted on unmount (or when the id changes).
 */
export function useDeveloperStatusStream(id: string | undefined): void {
  const qc = useQueryClient();
  useEffect(() => {
    if (!id) return;
    const controller = new AbortController();
    void (async () => {
      try {
        for await (const { event, data } of streamDeveloperEvents(
          id,
          controller.signal,
        )) {
          if (event !== 'status') continue;
          let status: DeveloperStatus | undefined;
          try {
            status = (JSON.parse(data) as { status?: DeveloperStatus }).status;
          } catch {
            continue;
          }
          if (!status) continue;
          qc.setQueryData(developerQueryOptions(id).queryKey, (prev) =>
            prev ? { ...prev, status } : prev,
          );
          qc.setQueryData(developersQueryOptions().queryKey, (prev) =>
            prev?.map((dev) => (dev.id === id ? { ...dev, status } : dev)),
          );
        }
      } catch {
        /** An abort on unmount is expected; polling remains the fallback. */
      }
    })();
    return () => controller.abort();
  }, [id, qc]);
}

/** The window a spending summary is scoped to. */
export type SpendingPeriod =
  'today' | 'week' | 'month' | 'quarter' | 'year' | 'all';

/** The selectable spending periods, shortest window first. */
export const PERIODS: ReadonlyArray<{ value: SpendingPeriod; label: string }> =
  [
    { value: 'today', label: 'Today' },
    { value: 'week', label: 'This Week' },
    { value: 'month', label: 'This Month' },
    { value: 'quarter', label: 'This Quarter' },
    { value: 'year', label: 'This Year' },
    { value: 'all', label: 'All Time' },
  ];

/**
 * A spending summary for one developer (or all developers, when `byDeveloper` is present): totals plus
 * the series that feed the dashboard charts. All costs are USD.
 */
export interface SpendingSummary {
  totalUsd: number;
  runCount: number;
  inputTokens: number;
  outputTokens: number;
  /** Input tokens read from the prompt cache. Absent or null on a service that predates it. */
  cacheReadTokens?: number | null;
  /** Input tokens written to the prompt cache. Absent or null on a service that predates it. */
  cacheCreationTokens?: number | null;
  trend: { bucket: string; costUsd: number }[];
  byActivityType: {
    key: string;
    label: string;
    costUsd: number;
    runCount: number;
  }[];
  byProject: { project: string; costUsd: number; runCount: number }[];
  topCategories: { key: string; label: string; costUsd: number }[];
  usageVsSpend: { tokens: number; costUsd: number }[];
  byDeveloper?: {
    developerId: string;
    name: string;
    costUsd: number;
    runCount: number;
  }[];
}

async function fetchSpending(
  id: string,
  period: SpendingPeriod,
): Promise<SpendingSummary> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(id)}/spending?period=${period}`,
    { headers: await authHeaders() },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to load spending'));
  return (await res.json()) as SpendingSummary;
}

async function fetchGlobalSpending(
  period: SpendingPeriod,
): Promise<SpendingSummary> {
  const res = await fetch(`${ENDPOINT}/spending?period=${period}`, {
    headers: await authHeaders(),
  });
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to load spending'));
  return (await res.json()) as SpendingSummary;
}

/** One developer's spending summary for a period — feeds the per-developer Spending tab. */
export function spendingQueryOptions(
  id: string | undefined,
  period: SpendingPeriod,
) {
  const queryKey: QueryKey = ['developers', id, 'spending', period];
  return queryOptions({
    queryKey,
    queryFn: () => fetchSpending(id as string, period),
    enabled: id !== undefined,
    staleTime: 60_000,
  });
}

/** The all-developers spending summary for a period — feeds the global Spending dashboard. */
export function globalSpendingQueryOptions(period: SpendingPeriod) {
  const queryKey: QueryKey = ['developers', 'spending', period];
  return queryOptions({
    queryKey,
    queryFn: () => fetchGlobalSpending(period),
    staleTime: 60_000,
  });
}

/** A chat conversation with a developer (admin Chat tab). */
export interface Conversation {
  id: string;
  title?: string;
  createdAt: string;
  updatedAt: string;
}

/** One message in a chat conversation transcript. */
export interface ChatMessage {
  id: string;
  role: 'user' | 'developer';
  body: string;
  runId?: string;
  createdAt: string;
  /** Files the user sent with this message. */
  attachments?: ChatAttachment[];
}

/** A file or image attached to a chat message — metadata only; the bytes come from the download route. */
export interface ChatAttachment {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
}

/** Largest chat attachment the service accepts — mirrors MAX_ATTACHMENT_BYTES in cti-developer. */
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/** A conversation plus its full message transcript. */
export interface ConversationDetail {
  conversation: Conversation;
  messages: ChatMessage[];
}

async function fetchConversations(id: string): Promise<Conversation[]> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(id)}/conversations`,
    { headers: await authHeaders() },
  );
  /** An older backend without the endpoint (404) or with it disabled (501) → no conversations, not an error. */
  if (res.status === 404 || res.status === 501) return [];
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to load conversations'));
  }
  return (await res.json()) as Conversation[];
}

async function fetchConversation(args: {
  id: string;
  conversationId: string;
}): Promise<ConversationDetail> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/conversations/${encodeURIComponent(args.conversationId)}`,
    { headers: await authHeaders() },
  );
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to load the conversation'));
  }
  return (await res.json()) as ConversationDetail;
}

async function createConversation(args: {
  id: string;
  fromRunId?: string;
}): Promise<Conversation> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/conversations`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(args.fromRunId ? { fromRunId: args.fromRunId } : {}),
    },
  );
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to start a conversation'));
  }
  return (await res.json()) as Conversation;
}

async function deleteConversation(args: {
  id: string;
  conversationId: string;
}): Promise<void> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/conversations/${encodeURIComponent(args.conversationId)}`,
    { method: 'DELETE', headers: await authHeaders() },
  );
  if (!res.ok) {
    throw new Error(
      await errorMessage(res, 'Failed to delete the conversation'),
    );
  }
}

async function uploadChatAttachment(args: {
  id: string;
  conversationId: string;
  file: File;
}): Promise<ChatAttachment> {
  const form = new FormData();
  form.append('file', args.file);
  const headers = await authHeaders();
  /** Let the browser set the multipart boundary — a hand-set Content-Type breaks the parse. */
  delete (headers as Record<string, string>)['Content-Type'];
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/conversations/${encodeURIComponent(args.conversationId)}/attachments`,
    { method: 'POST', headers, body: form },
  );
  if (res.status === 413) {
    throw new Error('That file is too large — the limit is 10 MB.');
  }
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to upload the file'));
  }
  return (await res.json()) as ChatAttachment;
}

/**
 * Fetch a chat attachment's bytes. This SPA is bearer-only, and a browser navigation to a plain
 * `<a href>` sends no Authorization header — the download would just 401. So the bytes come through
 * an authenticated fetch and the caller hands them to a temporary object URL, the same shape the
 * skill-export and activity-log downloads already use.
 */
export async function fetchChatAttachment(args: {
  id: string;
  conversationId: string;
  attachmentId: string;
}): Promise<Blob> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/conversations/${encodeURIComponent(args.conversationId)}/attachments/${encodeURIComponent(args.attachmentId)}`,
    { headers: await authHeaders() },
  );
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to download the file'));
  }
  return res.blob();
}

/**
 * Fetch a run's session archive as a zip `File`, named from the response's `Content-Disposition`.
 * Bearer-only like the chat attachments, so the bytes come through an authenticated fetch.
 */
export async function fetchSessionArchive(args: {
  developerId: string;
  runId: string;
}): Promise<File> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.developerId)}/runs/${encodeURIComponent(args.runId)}/session-archive`,
    { headers: await authHeaders() },
  );
  if (!res.ok) {
    throw new Error(
      await errorMessage(res, 'Could not download the session archive'),
    );
  }
  const filename = filenameFromContentDisposition(
    res,
    `session-${args.runId}.zip`,
  );
  return new File([await res.blob()], filename, { type: 'application/zip' });
}

async function postConversationMessage(args: {
  id: string;
  conversationId: string;
  body: string;
  attachmentIds?: string[];
}): Promise<{ run: DeveloperRun | null }> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/conversations/${encodeURIComponent(args.conversationId)}/messages`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify({
        body: args.body,
        ...(args.attachmentIds?.length
          ? { attachmentIds: args.attachmentIds }
          : {}),
      }),
    },
  );
  if (res.status === 404 || res.status === 501) {
    throw new Error('Chat conversations are not available for this developer.');
  }
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to send the message'));
  }
  return (await res.json()) as { run: DeveloperRun | null };
}

export function conversationsQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: ['developers', id, 'conversations'],
    queryFn: () => fetchConversations(id as string),
    enabled: id !== undefined,
    staleTime: 10_000,
  });
}

export function conversationQueryOptions(
  id: string | undefined,
  conversationId: string | undefined,
) {
  return queryOptions({
    queryKey: ['developers', id, 'conversations', conversationId],
    queryFn: () =>
      fetchConversation({
        id: id as string,
        conversationId: conversationId as string,
      }),
    enabled: id !== undefined && conversationId !== undefined,
  });
}

export function useCreateConversation(): UseMutationResult<
  Conversation,
  Error,
  { id: string; fromRunId?: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createConversation,
    onSuccess: (conversation, args) => {
      /** Show the new conversation in the list immediately (de-duped), before the refetch lands. */
      qc.setQueryData<Conversation[]>(
        ['developers', args.id, 'conversations'],
        (old) => [
          conversation,
          ...(old ?? []).filter((existing) => existing.id !== conversation.id),
        ],
      );
      void qc.invalidateQueries({
        queryKey: ['developers', args.id, 'conversations'],
      });
    },
  });
}

export function useDeleteConversation(): UseMutationResult<
  void,
  Error,
  { id: string; conversationId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteConversation,
    onSuccess: (_result, args) => {
      qc.setQueryData<Conversation[]>(
        ['developers', args.id, 'conversations'],
        (old) => (old ?? []).filter((one) => one.id !== args.conversationId),
      );
      void qc.invalidateQueries({
        queryKey: ['developers', args.id, 'conversations'],
      });
    },
  });
}

export function useUploadChatAttachment(): UseMutationResult<
  ChatAttachment,
  Error,
  { id: string; conversationId: string; file: File }
> {
  return useMutation({ mutationFn: uploadChatAttachment });
}

export function usePostConversationMessage(): UseMutationResult<
  { run: DeveloperRun | null },
  Error,
  {
    id: string;
    conversationId: string;
    body: string;
    attachmentIds?: string[];
  }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: postConversationMessage,
    onSuccess: (result, args) => {
      /** The conversations prefix also matches (and refetches) the open conversation-detail query. */
      void qc.invalidateQueries({
        queryKey: ['developers', args.id, 'conversations'],
      });
      /** Only refetch runs when the message actually started/resumed one. */
      if (result.run) {
        void qc.invalidateQueries({
          queryKey: ['developers', args.id, 'runs'],
        });
      }
    },
  });
}

/** One scheduled prompt, as the service serves it. */
export interface DeveloperSchedule {
  id: string;
  name: string;
  prompt: string;
  cron: string;
  timezone: string;
  enabled: boolean;
  /** Absent while the schedule is switched off — that is what the clock reads. */
  nextFireAt?: string;
  lastFiredAt?: string;
  lastRunId?: string;
  fireCount: number;
  createdAt: string;
  /**
   * The people CTI Secretary messages in Teams about this schedule's runs, as lowercased emails.
   * Optional because a service older than the field does not send it.
   */
  notifyEmails?: string[];
}

/** What the dialog posts. Create and update take the same body: both send the whole form. */
export interface ScheduleInput {
  name: string;
  prompt: string;
  cron: string;
  timezone: string;
  enabled: boolean;
  /**
   * The people to message in Teams about each run, as lowercased emails, at most 20. Left out, the
   * stored list stays as it is; an empty list clears it.
   */
  notifyEmails?: string[];
}

async function fetchSchedules(id: string): Promise<DeveloperSchedule[]> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}/schedules`, {
    headers: await authHeaders(),
  });
  /**
   * A Developer service without this route answers 404 — or 501 where the route exists and is
   * switched off — and a 404 is also what a developer this caller may not read answers. Every one of
   * those is honestly an empty tab rather than a failure, which is how `fetchSuggestions` and
   * `fetchConversations` already read the same pair, and what keeps this build usable against a
   * service that has not been redeployed yet.
   */
  if (res.status === 404 || res.status === 501) return [];
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to load the schedules'));
  }
  return (await res.json()) as DeveloperSchedule[];
}

async function createSchedule(args: {
  id: string;
  input: ScheduleInput;
}): Promise<DeveloperSchedule> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/schedules`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(args.input),
    },
  );
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to save the schedule'));
  }
  return (await res.json()) as DeveloperSchedule;
}

async function updateSchedule(args: {
  id: string;
  scheduleId: string;
  input: ScheduleInput;
}): Promise<DeveloperSchedule> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/schedules/${encodeURIComponent(args.scheduleId)}`,
    {
      method: 'PATCH',
      headers: await authHeaders(),
      body: JSON.stringify(args.input),
    },
  );
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to save the schedule'));
  }
  return (await res.json()) as DeveloperSchedule;
}

async function deleteSchedule(args: {
  id: string;
  scheduleId: string;
}): Promise<void> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/schedules/${encodeURIComponent(args.scheduleId)}`,
    { method: 'DELETE', headers: await authHeaders() },
  );
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to delete the schedule'));
  }
}

async function runSchedule(args: {
  id: string;
  scheduleId: string;
}): Promise<{ run: DeveloperRun | null }> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/schedules/${encodeURIComponent(args.scheduleId)}/run`,
    { method: 'POST', headers: await authHeaders() },
  );
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to run the schedule'));
  }
  return (await res.json()) as { run: DeveloperRun | null };
}

/**
 * A developer's scheduled prompts. Polled rather than merely made stale: the clock moves `nextFireAt`
 * and `lastRunId` with nothing happening on this page, and the app disables `refetchOnWindowFocus`,
 * so a `staleTime` alone would leave a tab reading a Next-run time that has already passed.
 */
export function schedulesQueryOptions(id: string | undefined) {
  return queryOptions({
    queryKey: ['developers', id, 'schedules'],
    queryFn: () => fetchSchedules(id as string),
    enabled: id !== undefined,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useSaveSchedule(): UseMutationResult<
  DeveloperSchedule,
  Error,
  { id: string; scheduleId?: string; input: ScheduleInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args) =>
      args.scheduleId
        ? updateSchedule({
            id: args.id,
            scheduleId: args.scheduleId,
            input: args.input,
          })
        : createSchedule({ id: args.id, input: args.input }),
    onSuccess: (_result, args) => {
      void qc.invalidateQueries({
        queryKey: ['developers', args.id, 'schedules'],
      });
    },
  });
}

export function useDeleteSchedule(): UseMutationResult<
  void,
  Error,
  { id: string; scheduleId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteSchedule,
    onSuccess: (_result, args) => {
      void qc.invalidateQueries({
        queryKey: ['developers', args.id, 'schedules'],
      });
    },
  });
}

/**
 * Run a schedule now. Everything the new run touches moves — the developer's status badge, its
 * sessions and History — so they are invalidated alongside the schedule list, which gains the run id.
 */
export function useRunSchedule(): UseMutationResult<
  { run: DeveloperRun | null },
  Error,
  { id: string; scheduleId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: runSchedule,
    onSuccess: (_result, args) => {
      void qc.invalidateQueries({
        queryKey: ['developers', args.id, 'schedules'],
      });
      void qc.invalidateQueries({
        queryKey: ['developers', args.id, 'sessions'],
      });
      /**
       * A fire opens a **chat thread**, and the Chat tab reads its list once without polling — so an
       * already-open tab would not show the new conversation until it remounted.
       */
      void qc.invalidateQueries({
        queryKey: ['developers', args.id, 'conversations'],
      });
      void qc.invalidateQueries({ queryKey: ['developers', args.id, 'runs'] });
      void qc.invalidateQueries({ queryKey: ['developers', args.id] });
      void qc.invalidateQueries({ queryKey: ['developers'] });
    },
  });
}

/** Where a run on the disk view came from. API-cast, so a newer server may send another value. */
export type DiskRunSource = 'jira' | 'github' | 'gitlab' | 'manual';

/** One run as the disk view names it: enough to say what it is and to link to its ticket. */
export interface DiskRunRef {
  id: string;
  status: RunStatus;
  triggerRef: string;
  /** The first line of the request, bounded by the service. */
  title: string;
  /** The Jira issue, when the run came from one. */
  ticketUrl?: string;
  branch?: string | null;
  source: DiskRunSource;
}

/** A run in the development queue, with why it waits and its place in line. */
export interface DiskWaitingRun extends DiskRunRef {
  reason: SlotWaitReason;
  /** When the run started waiting, ISO. */
  since: string;
  /** The 1-based place in the queue. */
  position: number;
}

/** One run's worktree on the developer's workspace volume. */
export interface DiskWorktree {
  runId: string;
  /** The checkout directory names inside the run's directory. */
  repos: string[];
  createdAt: string | null;
  /** Null until measured. */
  bytes: number | null;
  /** Null when the run no longer exists. */
  run: DiskRunRef | null;
  holdsSlot: boolean;
  /** The run is gone or finished, so nothing uses this disk any more. */
  orphaned: boolean;
}

/** The free and total space of the workspace volume, which every developer shares. */
export interface DiskVolume {
  freeBytes: number;
  totalBytes: number;
}

/** What the developer's workspace holds. Every size is null until the first measurement. */
export interface DiskUsage {
  /** When the sizes were last measured, ISO; null when they never were. */
  measuredAt: string | null;
  /** When the worktrees were last listed, ISO. */
  listedAt: string | null;
  totalBytes: number | null;
  /** The base clones plus the shared original checkouts. */
  originalsBytes: number | null;
  worktreesBytes: number | null;
  /** Everything else, such as auth stores and leftovers. */
  otherBytes: number | null;
  disk: DiskVolume | null;
  /** Below this much free space, development waits. */
  neededFreeBytes: number;
}

/** The developer's development slots: how many it has, who holds them and who waits for one. */
export interface DiskSlots {
  limit: number;
  inUse: number;
  holders: DiskRunRef[];
  waiting: DiskWaitingRun[];
}

/** Work the service accepted and has not done yet. */
export interface DiskPending {
  measure: boolean;
  /** The run ids whose worktree removal is requested and not done yet. */
  removals: string[];
}

/** One developer's disk: its usage, its slots and queue, and the worktrees on the volume. */
export interface DeveloperDiskView {
  slots: DiskSlots;
  usage: DiskUsage;
  worktrees: DiskWorktree[];
  pending: DiskPending;
}

/**
 * The developer's disk view. A 404 resolves to null, which the tab shows as "not available": an
 * older service has no such route, and a caller the service refuses gets the same answer.
 */
async function fetchDisk(id: string): Promise<DeveloperDiskView | null> {
  const res = await fetch(`${ENDPOINT}/${encodeURIComponent(id)}/disk`, {
    headers: await authHeaders(),
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to load the disk usage'));
  }
  return (await res.json()) as DeveloperDiskView;
}

/** Ask for a fresh size measurement. The answer carries `pending.measure` until it has run. */
async function measureDisk(id: string): Promise<DeveloperDiskView> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(id)}/disk/measure`,
    { method: 'POST', headers: await authHeaders() },
  );
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to start the measurement'));
  }
  return (await res.json()) as DeveloperDiskView;
}

/**
 * Delete one run's worktree. The service stops the run first when it is still active, and the answer
 * may list the worktree in `pending.removals` until the container that holds it acts.
 */
async function deleteWorktree(args: {
  id: string;
  runId: string;
}): Promise<DeveloperDiskView> {
  const res = await fetch(
    `${ENDPOINT}/${encodeURIComponent(args.id)}/worktrees/${encodeURIComponent(args.runId)}`,
    { method: 'DELETE', headers: await authHeaders() },
  );
  if (!res.ok) {
    throw new Error(await errorMessage(res, 'Failed to delete the worktree'));
  }
  return (await res.json()) as DeveloperDiskView;
}

/** Whether something on the disk view is about to change: a measurement, a removal or a queue. */
function diskIsChanging(view: DeveloperDiskView): boolean {
  return (
    view.pending.measure ||
    view.pending.removals.length > 0 ||
    view.slots.waiting.length > 0
  );
}

/**
 * One developer's disk view. It polls every 10 seconds while something on it is about to change and
 * every minute otherwise. A backend without the route is not polled at all: it will not grow one
 * while the tab is open.
 */
export function diskQueryOptions(id: string) {
  return queryOptions({
    queryKey: ['developers', id, 'disk'],
    queryFn: () => fetchDisk(id),
    staleTime: 10_000,
    refetchInterval: (query) => {
      const view = query.state.data;
      if (view === null) return false;
      return view && diskIsChanging(view) ? 10_000 : 60_000;
    },
  });
}

/**
 * Ask for a fresh disk measurement and show the answer at once. A disk read already in flight is
 * cancelled first: it answers from before the request and, landing after it, would hide the pending
 * measurement.
 */
export function useMeasureDisk(): UseMutationResult<
  DeveloperDiskView,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: measureDisk,
    onMutate: (id) =>
      qc.cancelQueries({ queryKey: diskQueryOptions(id).queryKey }),
    onSuccess: (view, id) => {
      qc.setQueryData(diskQueryOptions(id).queryKey, view);
    },
  });
}

/**
 * Delete one run's worktree. The answer goes into the disk cache, and everything under the developers
 * key refreshes, because stopping an active run moves its status, the sessions and the badge.
 */
export function useDeleteWorktree(): UseMutationResult<
  DeveloperDiskView,
  Error,
  { id: string; runId: string }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteWorktree,
    onSuccess: (view, args) => {
      qc.setQueryData(diskQueryOptions(args.id).queryKey, view);
      void qc.invalidateQueries({ queryKey: ['developers'] });
    },
  });
}
